import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Button } from '../ui/button';
import { Slider } from '../ui/slider';
import { Progress } from '../ui/progress';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { useSubscription } from '../../context/SubscriptionContext';
import { useTheme } from '../../context/ThemeContext';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import lamejs from 'lamejs';

// Import modular DAW components
import {
  DAWOverlays,
  StatusBar,
  InlineTrackControls,
  InlineTrackLane,
  InlineTimeRuler,
  InlineEffectsPanel,
  MIDIButtonWithPanel,
  ExportButtons,
  ProjectControls,
  TimelineHeader,
  TrackSidebar,
  TransportControls,
  EditingTools,
  LoopToggle,
  GridSnapToggle,
  AutomationToggle,
  InputMonitorToggle,
  MetronomeIcon,
  InstrumentsToggle,
  UndoRedoButtons,
  SmartTempoToggle,
  ChordTrackToggle,
  ChordWizardButton,
  EffectsToggle,
  MasteringToggle,
  PluginRackToggle,
  QuickStartButton,
  AIMixingToggle,
  AIMasteringToggle,
  CollaborationButton,
  JamSessionButton,
  AIMixAssistantButton,
  AIChordGeneratorButton,
  AILyricAssistantButton,
  AIDrumGeneratorButton,
  VideoSyncButton,
  SampleLibraryButton,
  PluginBrowserButton,
  PluginMarketplaceButton,
  KeyboardShortcutsButton,
  ThemeToggleButton,
  DuplicateProjectButton,
  UserMenu,
  MobileMixerToggle,
  LoopRegionOverlay,
  Playhead,
  BPMDisplay,
  TimeDisplay,
  PlaybackControls,
  ProjectHeader,
  MetronomeToggle,
  MainTransportButtons,
  AdvancedEffectsModal,
  MarkersPanelWrapper,
  MarkerPickerWrapper,
  AIChordGeneratorWrapper,
  AIDrumGeneratorWrapper,
  SampleLibraryWrapper,
  AIMixingPanelWrapper,
  MasteringPanelWrapper,
  AIMasteringPanelWrapper,
  CollaborationPanelWrapper,
  SmartTempoPanelWrapper,
  EffectsPanelWrapper,
  JamSessionPanelWrapper,
  PluginRackPanel,
  PianoRollWrapper,
  SessionManagerWrapper,
  JamSessionModalWrapper,
  PluginMarketplaceWrapper,
  BatchExporterWrapper,
  VideoSyncPanelWrapper,
  ChordWizardWrapper,
  QuickStartWrapper,
  QuickMixProPanel,
  AILyricAssistantWrapper,
  AIMixAssistantWrapper,
  EffectPresetsBrowserWrapper,
  PluginBrowserWrapper,
  ChainPresetSelectorWrapper,
  KeyboardShortcutsPanelWrapper,
  WaveformToolbarWrapper,
  InstrumentsPanelWrapper,
  RemoteCursorsWrapper,
  HeaderToolbar,
  TimelineContent,
  EffectsSidebarWrapper,
  MixerPanelWrapper,
  MixViewWrapper,
  TrackSidebarWrapper,
  DAWModalsContainer,
} from './daw';

// Import constants
import {
  MAX_TRACKS,
  SAMPLE_RATE,
  DEFAULT_BPM,
  TRACK_COLORS,
  audioBufferToBase64,
  base64ToAudioBuffer,
  splitIntoChunks,
  CHUNK_SIZE,
} from './daw/index';

// Import custom hooks for modular state management
import {
  useUndoRedo,
  ActionTypes,
  useMixer,
  usePlugins,
  useAutomation,
  useKeyboardShortcuts,
  usePlayback,
  useRecording,
  useMIDI,
  useLoop,
  usePanelState,
} from './hooks';
import {
  Play,
  Pause,
  Square,
  Circle,
  Mic,
  Upload,
  Download,
  Save,
  FolderOpen,
  FileStack,
  Volume2,
  Flag,
  VolumeX,
  Headphones,
  Plus,
  Trash2,
  Settings,
  Undo2,
  Redo2,
  ZoomIn,
  ZoomOut,
  SkipBack,
  SkipForward,
  Loader2,
  ChevronLeft,
  ChevronRight,
  Sliders,
  X,
  Menu,
  Home,
  Copy,
  FileAudio,
  MousePointerClick,
  Piano,
  Drum,
  Usb,
  Repeat,
  Scissors,
  Merge,
  Grid3X3,
  Wand2,
  Sparkles,
  Activity,
  Music,
  Brain,
  Users,
  Gauge,
  Layers,
  Zap,
  User,
  LogOut,
  Crown,
  Keyboard,
  Radio,
  Film,
  Library,
  Plug,
  Store,
  Moon,
  Sun,
} from 'lucide-react';

// Keyboard Shortcuts Panel
import { KeyboardShortcutsPanel } from '../KeyboardShortcutsPanel';

// Quick Tour for new users
import { QuickTour, useQuickTour, RestartTourButton } from '../QuickTour';

// Contextual Tooltips
import { TooltipProvider, ContextualTooltip } from '../ContextualTooltip';

// Recent Projects
import { useRecentProjects, RecentProjectsButton } from '../RecentProjects';

// Tap Tempo
import { TapTempoControl } from '../TapTempo';

// Drag & Drop for track reordering
import { useDragDropTracks } from '../../hooks/useDragDropTracks';

// Project Templates
import { ProjectTemplateSelector, useProjectTemplate, PROJECT_TEMPLATES } from '../ProjectTemplates';

// Markers & Regions
import { 
  TimelineMarker, 
  TimelineRegion, 
  MarkersRegionsPanel, 
  MarkerTypePicker,
  useMarkersAndRegions,
  MARKER_TYPES,
} from '../MarkersRegions';

// MIDI Engine imports
import {
  SimpleSynth,
  DrumSynth,
  createMIDIEvent,
  MIDIEventType,
  DRUM_KIT,
  createInstrumentTrack,
} from './MIDIEngine';
import { InstrumentsPanel } from './InstrumentsPanel';
import { PianoRoll } from './PianoRoll';
import { MIDIClipView } from './MIDIClipView';
import {
  createMIDIClip,
  midiEventsToClip,
  duplicateMIDIClip,
  moveMIDIClip,
  trimMIDIClip,
  splitMIDIClip,
  joinMIDIClips,
} from './MIDIClip';
import { snapToGrid, conditionalSnap } from './GridSnap';
import { useMIDIDevices } from './useMIDIDevices';
import { MIDIDevicePanel } from './MIDIDevicePanel';
import { AutomationLanesContainer } from './AutomationLane';
import {
  createAutomationLane,
  createAutomationPoint,
  getValueAtTime,
  AUTOMATION_PARAMS,
  PLUGIN_AUTOMATION_PARAMS,
  getParamConfig,
  parseFullPluginParamKey,
} from './Automation';
import { WaveformClip } from './waveform/WaveformClip';
import { WaveformToolbar } from './waveform/WaveformToolbar';
import { MixerPanel, MixView, ViewTabs } from './mixer';
import { ChordWizard } from './chordwizard';
import { ChordTrack, createChordRegion } from './chordtrack';
import { SmartTempoPanel } from './smarttempo';
import { QuickStartTemplates, TEMPLATES } from './quickstart';
import { SimplifiedModeToggle, SIMPLIFIED_HIDDEN_FEATURES } from './simplifiedmode';
import { EffectsPanel as AdvancedEffectsPanel, GuitarEffectsChain, TrackGuitarEffects, AdvancedEffectsUI, EffectPresetsBrowser } from './effects';
import { AIMixingPanel } from './aimixing';
import { CollaborationPanel, RemoteCursors, TrackCollaboratorIndicator, TrackSelectionBorder, SessionManager } from './collaboration';
import { JamSessionModal, JamSessionPanel } from './jam';
import { AIMixAssistant, AIChordGenerator, AILyricAssistant, AIDrumPatternGenerator } from './ai';
import { VideoSyncPanel } from './video';
import { SampleLibraryBrowser } from './samples';
import { MasteringPanel, EXPORT_PRESETS, AIMasteringPanel } from './mastering';
import { 
  PluginBrowser, 
  PluginMarketplace,
  PluginRack, 
  pluginRegistry, 
  registerBuiltInPlugins,
  PluginChain,
  ChainPresetSelector,
  TrackPresetSelector,
} from './plugins';
import { BatchTrackExporter } from './tracks/BatchTrackExporter';

// Import utilities (only those not defined locally)
import {
  formatTime as formatTimeUtil,
  generateWaveform as generateWaveformUtil,
  generateWaveformPeaks,
  audioBufferToWav,
  MAX_HISTORY_DEPTH,
} from './utils';

// Create empty track (using TRACK_COLORS from daw/index.js)
const createEmptyTrack = (id, name) => ({
  id,
  name: name || `Track ${id + 1}`,
  audioBuffer: null,
  waveformData: [],
  volume: 1.0,
  pan: 0,
  muted: false,
  solo: false,
  eq: { bass: 0, mid: 0, treble: 0 },
  effects: { reverb: 0, delay: 0 },
  guitarEffects: null, // Guitar amp and pedal effects
  color: TRACK_COLORS[id % TRACK_COLORS.length],
  startTime: 0,
  duration: 0,
});

export const DAWWorkspace = () => {
  const { token, user, logout } = useAuth();
  const { getFeatureLimit, planId, isPro, isStudio } = useSubscription();
  const { isDark, toggleTheme } = useTheme();
  const navigate = useNavigate();
  
  // Get subscription-based track limit
  const subscriptionTrackLimit = getFeatureLimit('max_tracks') || 4;
  const effectiveMaxTracks = subscriptionTrackLimit === -1 ? MAX_TRACKS : Math.min(subscriptionTrackLimit, MAX_TRACKS);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  
  // Audio context and nodes
  const audioContextRef = useRef(null);
  const masterGainRef = useRef(null);
  const makeupGainRef = useRef(null);  // 15x volume boost
  const limiterRef = useRef(null);     // Prevent clipping
  const analyserRef = useRef(null);
  const trackNodesRef = useRef({});
  
  // Recording
  const mediaRecorderRef = useRef(null);
  const recordedChunksRef = useRef([]);
  
  // Playback
  const sourceNodesRef = useRef({});
  const playbackStartTimeRef = useRef(0);
  const animationFrameRef = useRef(null);
  
  // MIDI Clip Playback
  const scheduledMidiEventsRef = useRef([]); // Array of { timeoutId, trackId, noteId }
  const activeMidiNotesRef = useRef(new Map()); // Map of trackId -> Set of active note pitches
  
  // Track gain nodes for automation (tracks gainNode per track for real-time updates)
  const trackGainNodesRef = useRef({});
  
  // Guitar effects chains per track
  const guitarEffectChainsRef = useRef({});
  
  // State
  const [tracks, setTracks] = useState(() => 
    Array.from({ length: 4 }, (_, i) => createEmptyTrack(i))
  );
  const [expandedGuitarEffectsTrack, setExpandedGuitarEffectsTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [inputMonitorEnabled, setInputMonitorEnabled] = useState(false);
  const [inputLevel, setInputLevel] = useState(0);
  const [inputGain, setInputGain] = useState(8.0); // Input gain boost (8x default for USB interfaces)
  const [selectedAudioInput, setSelectedAudioInput] = useState(null); // Selected audio input device
  const [currentTime, setCurrentTime] = useState(0);
  const [totalDuration, setTotalDuration] = useState(60);
  const [bpm, setBpm] = useState(DEFAULT_BPM);
  const [masterVolume, setMasterVolume] = useState(1.0);
  const [selectedTrack, setSelectedTrack] = useState(0);
  
  // Get current track's guitar effects state for live monitoring
  // Priority: 1) Selected track with effects enabled, 2) Any track with effects enabled, 3) Selected track
  const currentTrackEffects = useMemo(() => {
    const selected = tracks.find(t => t.id === selectedTrack);
    if (selected?.guitarEffects?.enabled) return selected.guitarEffects;
    const trackWithEffects = tracks.find(t => t.guitarEffects?.enabled);
    if (trackWithEffects) return trackWithEffects.guitarEffects;
    return selected?.guitarEffects || null;
  }, [tracks, selectedTrack]);
  
  const [zoom, setZoom] = useState(1);
  const [projectName, setProjectName] = useState('Untitled Project');
  const [isSaving, setIsSaving] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [recordingTrack, setRecordingTrack] = useState(null);
  const inputMonitorNodeRef = useRef(null);
  const inputGainNodeRef = useRef(null); // Input gain node ref
  const inputAnalyserRef = useRef(null);
  const inputLevelAnimationRef = useRef(null);
  const liveMonitorStreamRef = useRef(null); // For live monitoring without recording
  const liveMonitorSourceRef = useRef(null);
  const liveEffectsChainRef = useRef(null); // Guitar effects for live monitoring
  const [monitorStreamReady, setMonitorStreamReady] = useState(0); // Incremented when stream finishes setup
  
  // Panel visibility toggles (extracted to usePanelState hook)
  const {
    showMixer, setShowMixer,
    showEffects, setShowEffects,
    showChordWizard, setShowChordWizard,
    showChordTrack, setShowChordTrack,
    showSmartTempo, setShowSmartTempo,
    showQuickStart, setShowQuickStart,
    isSimplifiedMode, setIsSimplifiedMode,
    showAIMixing, setShowAIMixing,
    showCollaboration, setShowCollaboration,
    showSessionManager, setShowSessionManager,
    showAdvancedEffects, setShowAdvancedEffects,
    showEffectPresetsBrowser, setShowEffectPresetsBrowser,
    showMastering, setShowMastering,
    showAIMastering, setShowAIMastering,
    showAIMixAssistant, setShowAIMixAssistant,
    showAIChordGenerator, setShowAIChordGenerator,
    showAILyricAssistant, setShowAILyricAssistant,
    showAIDrumGenerator, setShowAIDrumGenerator,
    showVideoSync, setShowVideoSync,
    showSampleLibrary, setShowSampleLibrary,
    showPluginRack, setShowPluginRack,
    showPluginBrowser, setShowPluginBrowser,
    showPluginMarketplace, setShowPluginMarketplace,
    showChainPresets, setShowChainPresets,
    showBatchExporter, setShowBatchExporter,
    showKeyboardShortcuts, setShowKeyboardShortcuts,
    showAutomation, setShowAutomation,
    showInstruments, setShowInstruments,
    showMIDIPanel, setShowMIDIPanel,
    showMarkersPanel, setShowMarkersPanel,
    showJamSessionModal, setShowJamSessionModal,
    showJamSessionPanel, setShowJamSessionPanel,
    showTemplateSelector, setShowTemplateSelector,
  } = usePanelState();
  
  const [chordRegions, setChordRegions] = useState([]); // Chord track data
  const [remoteCursors, setRemoteCursors] = useState({}); // Remote user cursors
  const [collabUsers, setCollabUsers] = useState([]); // Collaboration users list
  const [collabSessionId, setCollabSessionId] = useState(() => {
    try {
      return localStorage.getItem('collabSessionId') || null;
    } catch {
      return null;
    }
  });
  const [selectedEffectsTrackId, setSelectedEffectsTrackId] = useState(null);
  const [advancedEffectsData, setAdvancedEffectsData] = useState(null);
  const [audioCharacteristics, setAudioCharacteristics] = useState(null);
  const audioAnalyzerRef = useRef(null);
  const analysisIntervalRef = useRef(null);
  const [masterPluginChain, setMasterPluginChain] = useState([]);
  const [trackPluginBrowserTarget, setTrackPluginBrowserTarget] = useState(null);
  const [trackPresetTarget, setTrackPresetTarget] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [viewMode, setViewMode] = useState('edit');
  const masterPluginChainRef = useRef(null);
  
  // Jam Session data state
  const [activeJamSession, setActiveJamSession] = useState(null);
  
  // Quick Tour for new users
  const { showTour, startTour, closeTour, completeTour } = useQuickTour();
  
  // Recent Projects
  const { addRecentProject } = useRecentProjects();
  
  // Drag & Drop for track reordering
  const handleReorderTracks = useCallback((newTracks) => {
    // Update track IDs to maintain consistency
    const reorderedTracks = newTracks.map((track, index) => ({
      ...track,
      // Keep original ID but update display order
    }));
    setTracks(reorderedTracks);
    toast.success('Track order updated');
  }, []);
  
  const {
    draggedTrackId,
    dragOverTrackId,
    dropPosition,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    handleDragEnd,
  } = useDragDropTracks({ tracks, onReorder: handleReorderTracks });
  
  // Project Templates
  const { createTracksFromTemplate } = useProjectTemplate();
  
  // Handle template selection
  const handleSelectTemplate = useCallback((template) => {
    const newTracks = createTracksFromTemplate(template);
    // Apply TRACK_COLORS to each track
    const tracksWithColors = newTracks.map((track, index) => ({
      ...track,
      color: TRACK_COLORS[index % TRACK_COLORS.length],
    }));
    setTracks(tracksWithColors);
    setShowTemplateSelector(false);
    toast.success(`Created project from "${template.name}" template`);
  }, [createTracksFromTemplate]);
  
  // Markers & Regions
  const {
    markers,
    regions,
    selectedMarkerId,
    selectedRegionId,
    setSelectedMarkerId,
    setSelectedRegionId,
    addMarker,
    updateMarker,
    deleteMarker,
    addRegion,
    updateRegion,
    deleteRegion,
  } = useMarkersAndRegions();
  
  const [showMarkerPicker, setShowMarkerPicker] = useState(false);
  const [markerPickerPosition, setMarkerPickerPosition] = useState({ x: 0, y: 0 });
  const [pendingMarkerTime, setPendingMarkerTime] = useState(null);
  
  // Metronome state
  const [metronomeEnabled, setMetronomeEnabled] = useState(false);
  const [metronomeVolume, setMetronomeVolume] = useState(0.5);
  const [currentBeat, setCurrentBeat] = useState(0);
  const [timeSignature, setTimeSignature] = useState(4); // beats per measure
  const metronomeIntervalRef = useRef(null);
  const metronomeGainRef = useRef(null);
  
  // Project loading state
  const [projectId, setProjectId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [saveProgress, setSaveProgress] = useState(null); // { current, total } for progress indicator
  
  // Count-in state
  const [countInEnabled, setCountInEnabled] = useState(true);
  const [countInBeats, setCountInBeats] = useState(4); // Default 1 bar of 4/4
  const [isCountingIn, setIsCountingIn] = useState(false);
  const [countInProgress, setCountInProgress] = useState(0);
  const performCountInRef = useRef(null); // Ref to hold performCountIn callback for hooks
  
  // Export state
  const [exportFormat, setExportFormat] = useState('wav'); // 'wav' or 'mp3'
  const [exportProgress, setExportProgress] = useState(null);
  const [isMasteringExporting, setIsMasteringExporting] = useState(false);
  
  // MIDI / Instruments state (UI state only - MIDI logic handled by useMIDI hook)
  const [selectedInstrument, setSelectedInstrument] = useState('keyboard'); // 'keyboard' or 'drums'
  // Note: activeNotes, activePads, isMidiRecording, midiRecordingEvents, showPianoRoll,
  // editingClip, editingTrackId are now managed by useMIDI hook
  
  // Synth engines (passed to useMIDI hook)
  const synthRef = useRef(null);
  const drumSynthRef = useRef(null);
  
  // Loop/Region state
  const [loopEnabled, setLoopEnabled] = useState(false);
  const [loopStart, setLoopStart] = useState(0); // Loop start time in seconds
  const [loopEnd, setLoopEnd] = useState(8); // Loop end time in seconds (default 8 bars at 120 BPM)
  const [isDraggingLoopStart, setIsDraggingLoopStart] = useState(false);
  const [isDraggingLoopEnd, setIsDraggingLoopEnd] = useState(false);
  const [isDraggingLoopRegion, setIsDraggingLoopRegion] = useState(false);
  const loopDragStartRef = useRef({ x: 0, loopStart: 0, loopEnd: 0 });
  
  // Loop refs (for use in animation frame callbacks to avoid stale closure)
  const loopEnabledRef = useRef(loopEnabled);
  const loopStartRef = useRef(loopStart);
  const loopEndRef = useRef(loopEnd);
  
  // Keep loop refs in sync with state
  useEffect(() => {
    loopEnabledRef.current = loopEnabled;
    loopStartRef.current = loopStart;
    loopEndRef.current = loopEnd;
  }, [loopEnabled, loopStart, loopEnd]);
  
  // Undo/Redo state - using MAX_HISTORY_DEPTH from ./utils
  const undoStackRef = useRef([]);
  const redoStackRef = useRef([]);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const pushUndoActionRef = useRef(null); // Ref for hooks that need pushUndoAction before it's defined
  
  // Tap tempo state
  const [tapTempoActive, setTapTempoActive] = useState(false);
  const [tapTimes, setTapTimes] = useState([]);
  const [calculatedBpm, setCalculatedBpm] = useState(null);
  const tapTimeoutRef = useRef(null);
  
  // External MIDI Device state
  const [midiActivity, setMidiActivity] = useState(false);
  const midiActivityTimeoutRef = useRef(null);
  
  // Automation state
  const [automationSnapEnabled, setAutomationSnapEnabled] = useState(true);
  const automationIntervalRef = useRef(null);
  
  // Clip editing state (split/join)
  const [clipEditMode, setClipEditMode] = useState(null); // null | 'split' | 'join'
  const [selectedClipsForJoin, setSelectedClipsForJoin] = useState([]); // [{trackId, clipId}]
  
  // Grid snap state
  const [snapEnabled, setSnapEnabled] = useState(true); // Snap to beat grid by default
  
  // Waveform editing state
  const [selectedAudioClip, setSelectedAudioClip] = useState(null); // {trackId, clipId} or {trackId} for audio tracks
  const [audioClipboard, setAudioClipboard] = useState(null); // Copied audio data { buffer, startTime, duration }
  const [waveformEditMode, setWaveformEditMode] = useState(false); // Show waveform toolbar
  const [audioSelection, setAudioSelection] = useState(null); // { trackId, start, end } - selected region in seconds
  
  // ============== CUSTOM HOOKS ==============
  
  // Mixer controls hook - handles volume, pan, mute, solo, name changes
  const {
    handleVolumeChange: handleMixerVolumeChange,
    handlePanChange: handleMixerPanChange,
    handleMuteToggle: handleMixerMuteToggle,
    handleSoloToggle: handleMixerSoloToggle,
    handleNameChange: handleMixerNameChange,
    handleColorChange: handleMixerColorChange,
    handleArmToggle: handleMixerArmToggle,
  } = useMixer(setTracks, null, ActionTypes);
  
  // MIDI hook - handles MIDI note input, recording, and clip operations
  // Note: Refs may be null initially - hook handles this gracefully
  const {
    // State
    activeNotes,
    setActiveNotes,
    activePads,
    setActivePads,
    isMidiRecording,
    midiRecordingEvents,
    showPianoRoll,
    setShowPianoRoll,
    editingClip,
    setEditingClip,
    editingTrackId,
    setEditingTrackId,
    // Methods
    handleMidiNoteOn,
    handleMidiNoteOff,
    handleDrumHit,
    startMidiRecording,
    stopMidiRecording,
    openClipInPianoRoll,
    closePianoRoll,
    handlePianoRollClipUpdate,
    handleMoveClip,
    handleResizeClip,
    handleDeleteClip,
    handleDuplicateClip,
    createEmptyMidiClip,
    handleSplitMidiClip,
    handleJoinMidiClips,
    // External MIDI integration
    setMidiRecordingEvents,
    midiRecordStartTimeRef,
  } = useMIDI({
    audioContextRef,
    synthRef,
    drumSynthRef,
    tracks,
    setTracks,
    selectedTrack,
    pushUndoAction: (...args) => pushUndoActionRef.current?.(...args), // Delegate to ref
    ActionTypes,
    bpm,
    timeSignature,
    snapEnabled,
    countInEnabled,
    countInBeats,
    performCountIn: () => performCountInRef.current?.(), // Delegate to ref
    setIsCountingIn,
    setCountInProgress,
  });

  // Get project ID from URL params
  const [searchParams] = useSearchParams();
  
  // Detect mobile and iOS
  const [isIOS, setIsIOS] = useState(false);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    
    // Detect iOS
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    setIsIOS(iOS);
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Initialize Audio Context with iOS optimizations
  useEffect(() => {
    const initAudio = async () => {
      if (!audioContextRef.current) {
        // Detect iOS for optimal settings
        const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
                    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
        
        // Audio context settings optimized for stable playback
        // 'playback' latency hint gives larger buffers = more stable but slightly more latency
        // 'interactive' is lower latency but can cause dropouts
        const contextOptions = iOS ? {
          sampleRate: 44100, // iOS works best with 44.1kHz
          latencyHint: 'playback', // More stable on iOS
        } : {
          sampleRate: SAMPLE_RATE,
          latencyHint: 'playback', // Prioritize stability over low latency
        };
        
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)(contextOptions);
        
        // iOS requires user interaction to start audio - handle suspended state
        if (audioContextRef.current.state === 'suspended') {
          const resumeAudio = async () => {
            await audioContextRef.current.resume();
            document.removeEventListener('touchstart', resumeAudio);
            document.removeEventListener('touchend', resumeAudio);
            document.removeEventListener('click', resumeAudio);
          };
          document.addEventListener('touchstart', resumeAudio, { once: true });
          document.addEventListener('touchend', resumeAudio, { once: true });
          document.addEventListener('click', resumeAudio, { once: true });
        }
        
        // Create master gain
        masterGainRef.current = audioContextRef.current.createGain();
        masterGainRef.current.gain.value = masterVolume;
        
        // Create makeup gain (15x volume boost so output is very loud by default - user can turn down)
        makeupGainRef.current = audioContextRef.current.createGain();
        makeupGainRef.current.gain.value = 15.0;
        
        // Create limiter to prevent clipping from the boost
        limiterRef.current = audioContextRef.current.createDynamicsCompressor();
        limiterRef.current.threshold.value = -1;    // Limit only at near-max
        limiterRef.current.knee.value = 3;          // Tight knee for maximum loudness
        limiterRef.current.ratio.value = 20;        // Hard limiting
        limiterRef.current.attack.value = 0.001;    // Fast attack
        limiterRef.current.release.value = 0.05;    // Very quick release
        
        // Chain: masterGain -> makeupGain -> limiter -> destination
        masterGainRef.current.connect(makeupGainRef.current);
        makeupGainRef.current.connect(limiterRef.current);
        limiterRef.current.connect(audioContextRef.current.destination);
        
        // Create analyser for visualization (tap off the limiter output)
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
        limiterRef.current.connect(analyserRef.current);
        
        // Create metronome gain (separate from master for independent volume)
        metronomeGainRef.current = audioContextRef.current.createGain();
        metronomeGainRef.current.gain.value = metronomeVolume;
        metronomeGainRef.current.connect(audioContextRef.current.destination);
        
        // Initialize synth engines
        synthRef.current = new SimpleSynth(audioContextRef.current, masterGainRef.current);
        synthRef.current.setWaveform('sawtooth');
        drumSynthRef.current = new DrumSynth(audioContextRef.current, masterGainRef.current);
        
        // Initialize plugin system
        registerBuiltInPlugins();
        masterPluginChainRef.current = new PluginChain(audioContextRef.current);
      }
    };
    
    initAudio();
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      if (metronomeIntervalRef.current) {
        clearInterval(metronomeIntervalRef.current);
      }
      // Stop all synth notes on cleanup
      synthRef.current?.stopAll();
    };
  }, []);
  
  // Update master volume
  useEffect(() => {
    if (masterGainRef.current) {
      masterGainRef.current.gain.value = masterVolume;
    }
  }, [masterVolume]);
  
  // Update input gain in real-time
  useEffect(() => {
    if (inputGainNodeRef.current) {
      inputGainNodeRef.current.gain.value = inputGain;
    }
  }, [inputGain]);
  
  // ============== LIVE MONITORING (separated: stream vs effects routing) ==============
  
  // Effect 1: Stream management - starts/stops the audio stream and creates base audio nodes.
  // Does NOT handle effects routing. Only restarts when device or toggle changes.
  useEffect(() => {
    let cancelled = false;
    
    const startLiveMonitoring = async () => {
      if (!inputMonitorEnabled) {
        // Stop live monitoring - full cleanup
        if (liveMonitorStreamRef.current) {
          liveMonitorStreamRef.current.getTracks().forEach(track => track.stop());
          liveMonitorStreamRef.current = null;
        }
        if (liveMonitorSourceRef.current) {
          liveMonitorSourceRef.current.disconnect();
          liveMonitorSourceRef.current = null;
        }
        if (inputMonitorNodeRef.current) {
          inputMonitorNodeRef.current.disconnect();
          inputMonitorNodeRef.current = null;
        }
        if (inputGainNodeRef.current) {
          inputGainNodeRef.current.disconnect();
          inputGainNodeRef.current = null;
        }
        if (liveEffectsChainRef.current) {
          liveEffectsChainRef.current.dispose();
          liveEffectsChainRef.current = null;
        }
        if (inputLevelAnimationRef.current) {
          cancelAnimationFrame(inputLevelAnimationRef.current);
          inputLevelAnimationRef.current = null;
        }
        setInputLevel(0);
        return;
      }
      
      try {
        // Initialize audio context if needed
        if (!audioContextRef.current) {
          audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
            sampleRate: 44100,
            latencyHint: 'playback',
          });
        }
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
        
        // Auto-detect device if none selected
        let deviceToUse = selectedAudioInput;
        if (!deviceToUse) {
          try {
            const tempStream = await navigator.mediaDevices.getUserMedia({ audio: true });
            tempStream.getTracks().forEach(t => t.stop());
            const devices = await navigator.mediaDevices.enumerateDevices();
            const audioInputs = devices.filter(d => d.kind === 'audioinput');
            const externalDevice = audioInputs.find(d => 
              d.label && 
              !d.label.toLowerCase().includes('built-in') && 
              !d.label.toLowerCase().includes('default') &&
              !d.label.toLowerCase().includes('internal')
            );
            if (externalDevice) {
              deviceToUse = externalDevice.deviceId;
              setSelectedAudioInput(externalDevice.deviceId);
              toast.success(`Using: ${externalDevice.label}`);
            } else if (audioInputs.length > 0) {
              deviceToUse = audioInputs[0].deviceId;
              setSelectedAudioInput(audioInputs[0].deviceId);
              toast.info(`Using default mic. Click mic to change.`);
            }
          } catch (e) {
          }
        }
        
        if (cancelled) return;
        
        const audioConstraints = {
          audio: {
            ...(deviceToUse ? { deviceId: { exact: deviceToUse } } : {}),
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 2,
            sampleRate: 44100,
          }
        };
        
        const stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        liveMonitorStreamRef.current = stream;
        
        const ctx = audioContextRef.current;
        
        // Build base audio chain: source -> highpass -> gain -> [effects slot] -> monitor -> destination
        const sourceNode = ctx.createMediaStreamSource(stream);
        liveMonitorSourceRef.current = sourceNode;
        
        const highPassFilter = ctx.createBiquadFilter();
        highPassFilter.type = 'highpass';
        highPassFilter.frequency.value = 80;
        highPassFilter.Q.value = 0.7;
        sourceNode.connect(highPassFilter);
        
        const gainNode = ctx.createGain();
        gainNode.gain.value = inputGain;
        highPassFilter.connect(gainNode);
        inputGainNodeRef.current = gainNode;
        
        // Analyser for level meter (dead-end tap, doesn't affect audio path)
        const analyserNode = ctx.createAnalyser();
        analyserNode.fftSize = 256;
        analyserNode.smoothingTimeConstant = 0.8;
        gainNode.connect(analyserNode);
        inputAnalyserRef.current = analyserNode;
        
        // Monitor output node - always connects to destination
        const monitorGainNode = ctx.createGain();
        monitorGainNode.gain.value = 1.0;
        inputMonitorNodeRef.current = monitorGainNode;
        monitorGainNode.connect(ctx.destination);
        
        // Default: direct connection (effects routing effect will rewire if needed)
        gainNode.connect(monitorGainNode);
        
        // Signal that stream is ready - this triggers the effects routing effect
        setMonitorStreamReady(v => v + 1);
        
        // Level meter animation
        let lastUpdateTime = 0;
        const updateInputLevel = (timestamp) => {
          if (!inputAnalyserRef.current) return;
          if (timestamp - lastUpdateTime > 50) {
            const dataArray = new Uint8Array(inputAnalyserRef.current.frequencyBinCount);
            inputAnalyserRef.current.getByteFrequencyData(dataArray);
            const average = dataArray.reduce((a, b) => a + b, 0) / dataArray.length;
            setInputLevel(average / 255);
            lastUpdateTime = timestamp;
          }
          inputLevelAnimationRef.current = requestAnimationFrame(updateInputLevel);
        };
        requestAnimationFrame(updateInputLevel);
        
        toast.success('Live monitoring enabled - play your guitar!');
      } catch (error) {
        console.error('Failed to start live monitoring:', error);
        if (error.name === 'NotFoundError') {
          toast.error('Audio device not found. Click mic to select your JamLab first.');
        } else if (error.name === 'NotAllowedError') {
          toast.error('Please allow microphone access in your browser');
        } else {
          toast.error('Could not start audio monitoring: ' + error.message);
        }
        setInputMonitorEnabled(false);
      }
    };
    
    startLiveMonitoring();
    
    return () => {
      cancelled = true;
      if (liveMonitorStreamRef.current) {
        liveMonitorStreamRef.current.getTracks().forEach(track => track.stop());
        liveMonitorStreamRef.current = null;
      }
      if (liveMonitorSourceRef.current) {
        liveMonitorSourceRef.current.disconnect();
        liveMonitorSourceRef.current = null;
      }
      if (inputMonitorNodeRef.current) {
        inputMonitorNodeRef.current.disconnect();
        inputMonitorNodeRef.current = null;
      }
      if (inputGainNodeRef.current) {
        inputGainNodeRef.current.disconnect();
        inputGainNodeRef.current = null;
      }
      if (liveEffectsChainRef.current) {
        liveEffectsChainRef.current.dispose();
        liveEffectsChainRef.current = null;
      }
      if (inputLevelAnimationRef.current) {
        cancelAnimationFrame(inputLevelAnimationRef.current);
        inputLevelAnimationRef.current = null;
      }
      setInputLevel(0);
      setMonitorStreamReady(0);
    };
    // inputGain intentionally excluded - handled by separate gain effect (line ~790)
    // isRecording intentionally excluded - live monitoring stays active during recording
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputMonitorEnabled, selectedAudioInput]);
  
  // Effect 2: Effects routing - rewires the audio chain when effects change.
  // Does NOT restart the stream. Instantly connects/disconnects effects nodes.
  // Uses a serialized key to reliably detect ANY change in effects config.
  const effectsConfigKey = useMemo(() => {
    const fx = currentTrackEffects;
    if (!fx?.enabled) return 'off';
    return JSON.stringify({
      enabled: fx.enabled,
      ampEnabled: fx.ampEnabled,
      ampModel: fx.ampModel,
      cabinet: fx.cabinet,
      ampControls: fx.ampControls,
      pedals: fx.pedals?.map(p => ({ type: p.type, enabled: p.enabled, params: p.params })),
    });
  }, [currentTrackEffects]);
  
  useEffect(() => {
    const gainNode = inputGainNodeRef.current;
    const monitorNode = inputMonitorNodeRef.current;
    
    // Only route effects when monitoring is active and audio nodes exist
    if (!inputMonitorEnabled || !gainNode || !monitorNode || !audioContextRef.current) return;
    
    // Dispose old effects chain
    if (liveEffectsChainRef.current) {
      liveEffectsChainRef.current.dispose();
      liveEffectsChainRef.current = null;
    }
    
    // Disconnect gain node from all downstream (except analyser - reconnect it after)
    const analyser = inputAnalyserRef.current;
    gainNode.disconnect();
    // Reconnect analyser tap
    if (analyser) gainNode.connect(analyser);
    
    const trackEffects = currentTrackEffects;
    
    if (trackEffects?.enabled) {
      // Create fresh effects chain
      const effectsChain = new GuitarEffectsChain(audioContextRef.current);
      effectsChain.enable();
      
      if (trackEffects.ampEnabled) {
        effectsChain.setAmpEnabled(true, {
          ampModel: trackEffects.ampModel,
          cabinet: trackEffects.cabinet,
          controls: trackEffects.ampControls,
        });
      }
      
      if (trackEffects.pedals && trackEffects.pedals.length > 0) {
        effectsChain.setPedals(trackEffects.pedals);
      }
      
      // Route: gain -> effects -> monitor
      gainNode.connect(effectsChain.input);
      effectsChain.output.connect(monitorNode);
      liveEffectsChainRef.current = effectsChain;
      
      const ampName = trackEffects.ampEnabled ? (trackEffects.ampModel || 'Custom') : 'Off';
      const pedalCount = trackEffects.pedals?.filter(p => p.enabled).length || 0;
      toast.success(`Effects: Amp=${ampName}, ${pedalCount} pedal(s)`);
    } else {
      // Direct: gain -> monitor
      gainNode.connect(monitorNode);
    }
    // effectsConfigKey is a serialized string that changes when ANY effect setting changes
    // monitorStreamReady signals that the stream finished async setup and nodes are available
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectsConfigKey, inputMonitorEnabled, monitorStreamReady]);
  
  // Calculate total duration from tracks
  useEffect(() => {
    const maxDuration = Math.max(
      60,
      ...tracks.map(t => t.startTime + t.duration)
    );
    setTotalDuration(maxDuration);
  }, [tracks]);
  
  // Loop marker drag handlers
  useEffect(() => {
    if (!isDraggingLoopStart && !isDraggingLoopEnd && !isDraggingLoopRegion) return;
    
    const MIN_LOOP_DURATION = 0.5; // Minimum loop duration in seconds
    
    const handleMouseMove = (e) => {
      const timeline = document.querySelector('[data-testid="tracks-timeline"]');
      if (!timeline) return;
      
      const rect = timeline.getBoundingClientRect();
      const deltaX = e.clientX - loopDragStartRef.current.x;
      const deltaTime = (deltaX / rect.width) * totalDuration / zoom;
      
      if (isDraggingLoopStart) {
        const newStart = Math.max(0, Math.min(loopDragStartRef.current.loopStart + deltaTime, loopEnd - MIN_LOOP_DURATION));
        setLoopStart(newStart);
      } else if (isDraggingLoopEnd) {
        const newEnd = Math.max(loopStart + MIN_LOOP_DURATION, Math.min(loopDragStartRef.current.loopEnd + deltaTime, totalDuration));
        setLoopEnd(newEnd);
      } else if (isDraggingLoopRegion) {
        const duration = loopDragStartRef.current.loopEnd - loopDragStartRef.current.loopStart;
        const newStart = Math.max(0, Math.min(loopDragStartRef.current.loopStart + deltaTime, totalDuration - duration));
        setLoopStart(newStart);
        setLoopEnd(newStart + duration);
      }
    };
    
    const handleMouseUp = () => {
      // Apply grid snapping on mouse up (final position)
      if (snapEnabled) {
        if (isDraggingLoopStart) {
          const snappedStart = conditionalSnap(loopStart, true, bpm, timeSignature);
          // Ensure minimum duration after snap
          const newStart = Math.min(snappedStart, loopEnd - MIN_LOOP_DURATION);
          setLoopStart(Math.max(0, newStart));
        } else if (isDraggingLoopEnd) {
          const snappedEnd = conditionalSnap(loopEnd, true, bpm, timeSignature);
          // Ensure minimum duration after snap
          const newEnd = Math.max(snappedEnd, loopStart + MIN_LOOP_DURATION);
          setLoopEnd(Math.min(totalDuration, newEnd));
        } else if (isDraggingLoopRegion) {
          // Snap the start, maintain duration
          const duration = loopEnd - loopStart;
          const snappedStart = conditionalSnap(loopStart, true, bpm, timeSignature);
          const clampedStart = Math.max(0, Math.min(snappedStart, totalDuration - duration));
          setLoopStart(clampedStart);
          setLoopEnd(clampedStart + duration);
        }
      }
      
      setIsDraggingLoopStart(false);
      setIsDraggingLoopEnd(false);
      setIsDraggingLoopRegion(false);
    };
    
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDraggingLoopStart, isDraggingLoopEnd, isDraggingLoopRegion, loopStart, loopEnd, totalDuration, zoom, snapEnabled, bpm, timeSignature]);
  
  // Track if this is the initial mount for loop notification
  const loopInitialMountRef = useRef(true);
  
  // Loop toggle notification - only fires when loopEnabled changes
  useEffect(() => {
    // Skip notification on initial mount
    if (loopInitialMountRef.current) {
      loopInitialMountRef.current = false;
      return;
    }
    
    // Show toast with current loop boundaries
    if (loopEnabled) {
      toast.info(`Loop enabled: ${formatTime(loopStart)} - ${formatTime(loopEnd)}`);
    } else {
      toast.info('Loop disabled');
    }
  }, [loopEnabled, loopStart, loopEnd]);
  
  // ============== EXTERNAL MIDI DEVICE INTEGRATION ==============
  
  // Handle external MIDI note on
  const handleExternalMidiNoteOn = useCallback((pitch, velocity, channel) => {
    // Show activity indicator
    setMidiActivity(true);
    if (midiActivityTimeoutRef.current) {
      clearTimeout(midiActivityTimeoutRef.current);
    }
    midiActivityTimeoutRef.current = setTimeout(() => setMidiActivity(false), 200);
    
    // Find the selected instrument track
    const track = tracks.find(t => t.id === selectedTrack && t.type === 'instrument');
    if (!track) {
      // No instrument track selected, just play through synth for monitoring
      if (synthRef.current) {
        synthRef.current.noteOn(pitch, velocity);
        setActiveNotes(prev => [...prev.filter(n => n !== pitch), pitch]);
      }
      return;
    }
    
    // Play through appropriate synth based on track type
    if (track.instrumentType === 'drums') {
      // Find drum from pitch
      const drumEntry = Object.entries(DRUM_KIT).find(([_, d]) => d.note === pitch);
      if (drumEntry && drumSynthRef.current) {
        drumSynthRef.current.playDrum(drumEntry[0], velocity);
        setActivePads(prev => [...prev.filter(p => p !== drumEntry[0]), drumEntry[0]]);
        setTimeout(() => setActivePads(prev => prev.filter(p => p !== drumEntry[0])), 100);
      }
    } else {
      if (synthRef.current) {
        synthRef.current.noteOn(pitch, velocity);
        setActiveNotes(prev => [...prev.filter(n => n !== pitch), pitch]);
      }
    }
    
    // Record if MIDI recording is active
    if (isMidiRecording) {
      const ctx = audioContextRef.current;
      if (ctx) {
        const time = ctx.currentTime - midiRecordStartTimeRef.current;
        setMidiRecordingEvents(prev => [
          ...prev,
          createMIDIEvent(pitch, velocity, time, 'noteOn'),
        ]);
      }
    }
  }, [tracks, selectedTrack, isMidiRecording]);
  
  // Handle external MIDI note off
  const handleExternalMidiNoteOff = useCallback((pitch, velocity, channel) => {
    // Find the selected instrument track
    const track = tracks.find(t => t.id === selectedTrack && t.type === 'instrument');
    
    // Release note (drums are one-shot, so only synth needs note off)
    if (!track || track.instrumentType !== 'drums') {
      if (synthRef.current) {
        synthRef.current.noteOff(pitch);
        setActiveNotes(prev => prev.filter(n => n !== pitch));
      }
    }
    
    // Record note off if MIDI recording is active
    if (isMidiRecording) {
      const ctx = audioContextRef.current;
      if (ctx) {
        const time = ctx.currentTime - midiRecordStartTimeRef.current;
        setMidiRecordingEvents(prev => [
          ...prev,
          createMIDIEvent(pitch, velocity, time, 'noteOff'),
        ]);
      }
    }
  }, [tracks, selectedTrack, isMidiRecording]);
  
  // Handle external MIDI control change
  const handleExternalMidiCC = useCallback((controller, value, channel) => {
    // Could be used for controlling parameters like volume, pan, etc.
    // For now, just log CC messages
    
    // Example: CC 1 (Mod wheel) could control filter cutoff
    // Example: CC 7 (Volume) could control track volume
  }, []);
  
  // Initialize MIDI devices hook
  const midiDevices = useMIDIDevices({
    onNoteOn: handleExternalMidiNoteOn,
    onNoteOff: handleExternalMidiNoteOff,
    onControlChange: handleExternalMidiCC,
  });
  
  // Load project from URL parameter
  const loadProject = useCallback(async (id) => {
    if (!token || !audioContextRef.current) return;
    
    setIsLoading(true);
    
    // Clear undo/redo history on project load
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
    
    try {
      // Fetch project metadata
      const response = await fetch(`${API_URL}/api/studio/projects/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!response.ok) {
        throw new Error('Project not found');
      }
      
      const project = await response.json();
      
      // Set project metadata
      setProjectId(project.id);
      setProjectName(project.name);
      setBpm(project.bpm || DEFAULT_BPM);
      setMasterVolume(project.masterVolume || 0.8);
      
      // Load tracks with audio
      const loadedTracks = await Promise.all(
        (project.tracks || []).map(async (trackData, index) => {
          const track = {
            ...createEmptyTrack(trackData.id),
            ...trackData,
            color: TRACK_COLORS[trackData.id % TRACK_COLORS.length],
            audioBuffer: null,
            waveformData: [],
          };
          
          // Load audio if track has audio
          if (trackData.hasAudio) {
            try {
              const audioResponse = await fetch(
                `${API_URL}/api/studio/audio/${id}/${trackData.id}`,
                { headers: { Authorization: `Bearer ${token}` } }
              );
              
              if (audioResponse.ok) {
                const audioData = await audioResponse.json();
                if (audioData.audio_data) {
                  const audioBuffer = await base64ToAudioBuffer(
                    audioData.audio_data,
                    audioContextRef.current
                  );
                  track.audioBuffer = audioBuffer;
                  track.waveformData = generateWaveform(audioBuffer);
                  track.duration = audioBuffer.duration;
                }
              }
            } catch (audioErr) {
              console.error(`Failed to load audio for track ${trackData.id}:`, audioErr);
            }
          }
          
          return track;
        })
      );
      
      setTracks(loadedTracks.length > 0 ? loadedTracks : [createEmptyTrack(0)]);
      toast.success(`Loaded: ${project.name}`);
      
      // Add to recent projects
      addRecentProject({
        id: project.id,
        name: project.name,
        tracks: loadedTracks,
      });
      
    } catch (error) {
      console.error('Error loading project:', error);
      toast.error('Failed to load project');
    } finally {
      setIsLoading(false);
    }
  }, [token, API_URL]);
  
  // Check URL for project ID on mount
  useEffect(() => {
    const urlProjectId = searchParams.get('project');
    if (urlProjectId && token) {
      // Ensure audio context is initialized
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: SAMPLE_RATE,
        });
        masterGainRef.current = audioContextRef.current.createGain();
        masterGainRef.current.gain.value = masterVolume;
        
        // Add 15x makeup gain + limiter (same as main init)
        makeupGainRef.current = audioContextRef.current.createGain();
        makeupGainRef.current.gain.value = 15.0;
        limiterRef.current = audioContextRef.current.createDynamicsCompressor();
        limiterRef.current.threshold.value = -1;
        limiterRef.current.knee.value = 3;
        limiterRef.current.ratio.value = 20;
        limiterRef.current.attack.value = 0.001;
        limiterRef.current.release.value = 0.05;
        masterGainRef.current.connect(makeupGainRef.current);
        makeupGainRef.current.connect(limiterRef.current);
        limiterRef.current.connect(audioContextRef.current.destination);
        
        analyserRef.current = audioContextRef.current.createAnalyser();
        analyserRef.current.fftSize = 2048;
        limiterRef.current.connect(analyserRef.current);
        metronomeGainRef.current = audioContextRef.current.createGain();
        metronomeGainRef.current.gain.value = metronomeVolume;
        metronomeGainRef.current.connect(audioContextRef.current.destination);
        synthRef.current = new SimpleSynth(audioContextRef.current, masterGainRef.current);
        synthRef.current.setWaveform('sawtooth');
        drumSynthRef.current = new DrumSynth(audioContextRef.current, masterGainRef.current);
      }
      loadProject(urlProjectId);
    }
  }, [searchParams, token, loadProject, masterVolume, metronomeVolume]);
  
  // Check URL for collaboration invite link
  useEffect(() => {
    const collabParam = searchParams.get('collab');
    if (collabParam && !collabSessionId) {
      // Auto-join collaboration session from URL
      setCollabSessionId(collabParam);
      setShowCollaboration(true);
      toast.info(`Joining collaboration session: ${collabParam}`);
      
      // Clear the URL param after joining
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('collab');
      window.history.replaceState({}, '', newUrl.toString());
    }
  }, [searchParams, collabSessionId]);
  
  // Check URL for template parameter - runs once on mount
  useEffect(() => {
    const templateParam = searchParams.get('template');
    if (templateParam) {
      const template = PROJECT_TEMPLATES.find(t => t.id === templateParam);
      if (template) {
        // Apply template
        const newTracks = createTracksFromTemplate(template);
        const tracksWithColors = newTracks.map((track, index) => ({
          ...track,
          color: TRACK_COLORS[index % TRACK_COLORS.length],
        }));
        setTracks(tracksWithColors);
        toast.success(`Created project from "${template.name}" template`);
      }
      
      // Clear the URL param after applying
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.delete('template');
      window.history.replaceState({}, '', newUrl.toString());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount
  
  // Metronome click sound generator
  const playMetronomeClick = useCallback((isAccent = false) => {
    if (!audioContextRef.current || !metronomeGainRef.current) return;
    
    const ctx = audioContextRef.current;
    const now = ctx.currentTime;
    
    // Create oscillator for click sound
    const osc = ctx.createOscillator();
    const gainNode = ctx.createGain();
    
    // Different frequency for accent (first beat) vs regular beat
    osc.frequency.value = isAccent ? 1000 : 800;
    osc.type = 'sine';
    
    // Quick attack and decay for click sound
    gainNode.gain.setValueAtTime(0, now);
    gainNode.gain.linearRampToValueAtTime(metronomeVolume, now + 0.001);
    gainNode.gain.exponentialRampToValueAtTime(0.001, now + 0.05);
    
    osc.connect(gainNode);
    gainNode.connect(metronomeGainRef.current);
    
    osc.start(now);
    osc.stop(now + 0.05);
  }, [metronomeVolume]);
  
  // Update metronome volume
  useEffect(() => {
    if (metronomeGainRef.current) {
      metronomeGainRef.current.gain.value = metronomeVolume;
    }
  }, [metronomeVolume]);
  
  // Metronome control - start/stop based on play state and metronome enabled
  useEffect(() => {
    if (metronomeEnabled && isPlaying) {
      // Calculate interval in ms from BPM
      const intervalMs = (60 / bpm) * 1000;
      let beatCount = 0;
      
      // Play first click immediately
      playMetronomeClick(true);
      setCurrentBeat(1);
      
      // Set up interval for subsequent clicks
      metronomeIntervalRef.current = setInterval(() => {
        beatCount = (beatCount + 1) % timeSignature;
        const isAccent = beatCount === 0;
        playMetronomeClick(isAccent);
        setCurrentBeat(beatCount + 1);
      }, intervalMs);
      
      return () => {
        if (metronomeIntervalRef.current) {
          clearInterval(metronomeIntervalRef.current);
          metronomeIntervalRef.current = null;
        }
        setCurrentBeat(0);
      };
    } else {
      // Clear interval when stopped or disabled
      if (metronomeIntervalRef.current) {
        clearInterval(metronomeIntervalRef.current);
        metronomeIntervalRef.current = null;
      }
      setCurrentBeat(0);
    }
  }, [metronomeEnabled, isPlaying, bpm, timeSignature, playMetronomeClick]);
  
  // ============== UNDO/REDO SYSTEM ==============
  
  // Action types imported from ./hooks/useUndoRedo
  // Using: ActionTypes from import at top
  
  // Push action to undo stack
  const pushUndoAction = useCallback((action) => {
    undoStackRef.current.push(action);
    
    // Limit stack size
    if (undoStackRef.current.length > MAX_HISTORY_DEPTH) {
      undoStackRef.current.shift();
    }
    
    // Clear redo stack on new action
    redoStackRef.current = [];
    
    // Update state
    setCanUndo(true);
    setCanRedo(false);
  }, []);
  
  // Update pushUndoActionRef so hooks can use it
  useEffect(() => {
    pushUndoActionRef.current = pushUndoAction;
  }, [pushUndoAction]);
  
  // Undo last action
  const undo = useCallback(() => {
    if (undoStackRef.current.length === 0) return;
    
    const action = undoStackRef.current.pop();
    
    // Execute undo
    switch (action.type) {
      case ActionTypes.ADD_TRACK:
        setTracks(prev => prev.filter(t => t.id !== action.trackId));
        break;
      case ActionTypes.REMOVE_TRACK:
        setTracks(prev => [...prev, action.track]);
        break;
      case ActionTypes.UPDATE_TRACK:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId ? { ...t, ...action.oldValues } : t
        ));
        break;
      case ActionTypes.SET_BPM:
        setBpm(action.oldValue);
        break;
      case ActionTypes.SET_MASTER_VOLUME:
        setMasterVolume(action.oldValue);
        break;
      case ActionTypes.IMPORT_AUDIO:
      case ActionTypes.RECORD_AUDIO:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, audioBuffer: action.oldAudioBuffer, waveformData: action.oldWaveformData, duration: action.oldDuration }
            : t
        ));
        break;
      case ActionTypes.ADD_INSTRUMENT_TRACK:
        setTracks(prev => prev.filter(t => t.id !== action.trackId));
        break;
      case ActionTypes.REMOVE_INSTRUMENT_TRACK:
        setTracks(prev => [...prev, action.track]);
        break;
      case ActionTypes.RECORD_MIDI:
        // Undo entire MIDI recording pass - restore old events
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiEvents: action.oldMidiEvents || [], midiClips: action.oldMidiClips || [] }
            : t
        ));
        break;
      // MIDI Clip undo actions
      case ActionTypes.ADD_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).filter(c => c.id !== action.clipId) }
            : t
        ));
        // Also update editing clip if it's the same
        if (editingClip?.id === action.clipId) {
          setEditingClip(null);
          setShowPianoRoll(false);
        }
        break;
      case ActionTypes.REMOVE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: [...(t.midiClips || []), action.clip] }
            : t
        ));
        break;
      case ActionTypes.UPDATE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === action.clipId ? action.oldClip : c) }
            : t
        ));
        // Update editing clip if it's the same
        if (editingClip?.id === action.clipId) {
          setEditingClip(action.oldClip);
        }
        break;
      case ActionTypes.MOVE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === action.clipId ? { ...c, startTime: action.oldStartTime } : c) }
            : t
        ));
        break;
      case ActionTypes.RESIZE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === action.clipId ? { ...c, duration: action.oldDuration } : c) }
            : t
        ));
        break;
      case ActionTypes.DUPLICATE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).filter(c => c.id !== action.newClipId) }
            : t
        ));
        break;
      // Piano Roll note undo actions
      case ActionTypes.PIANO_ROLL_ADD_NOTE:
      case ActionTypes.PIANO_ROLL_REMOVE_NOTE:
      case ActionTypes.PIANO_ROLL_MOVE_NOTE:
      case ActionTypes.PIANO_ROLL_RESIZE_NOTE:
      case ActionTypes.PIANO_ROLL_UPDATE_VELOCITY:
      case ActionTypes.PIANO_ROLL_QUANTIZE:
      case ActionTypes.PIANO_ROLL_DELETE_NOTES:
        // Restore the old clip state
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === action.clipId ? action.oldClip : c) }
            : t
        ));
        // Update editing clip if it's the same
        if (editingClip?.id === action.clipId) {
          setEditingClip(action.oldClip);
        }
        break;
      // Automation undo actions
      case ActionTypes.ADD_AUTOMATION_LANE:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, automationLanes: (t.automationLanes || []).filter(l => l.id !== action.laneId) }
            : t
        ));
        break;
      case ActionTypes.REMOVE_AUTOMATION_LANE:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, automationLanes: [...(t.automationLanes || []), action.lane] }
            : t
        ));
        break;
      case ActionTypes.UPDATE_AUTOMATION_LANE:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, automationLanes: (t.automationLanes || []).map(l => l.id === action.laneId ? action.oldLane : l) }
            : t
        ));
        break;
      // Clip split/join undo actions
      case ActionTypes.SPLIT_MIDI_CLIP:
        // Undo split: remove the two split clips, restore original
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { 
                ...t, 
                midiClips: [
                  ...(t.midiClips || []).filter(c => c.id !== action.leftClip.id && c.id !== action.rightClip.id),
                  action.originalClip,
                ]
              }
            : t
        ));
        break;
      case ActionTypes.JOIN_MIDI_CLIPS:
        // Undo join: remove merged clip, restore originals
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { 
                ...t, 
                midiClips: [
                  ...(t.midiClips || []).filter(c => c.id !== action.mergedClip.id),
                  ...action.originalClips,
                ]
              }
            : t
        ));
        break;
      case ActionTypes.SPLIT_AUDIO_CLIP:
        // Undo audio split: restore original track, remove the new right track
        setTracks(prev => prev
          .filter(t => t.id !== action.newTrackId)
          .map(t => 
            t.id === action.trackId 
              ? { ...t, ...action.originalTrackState, name: t.name.replace(' (L)', '') }
              : t
          )
        );
        break;
      default:
        break;
    }
    
    // Push to redo stack
    redoStackRef.current.push(action);
    
    // Update state
    setCanUndo(undoStackRef.current.length > 0);
    setCanRedo(true);
    
    toast.info('Undo');
  }, [editingClip]);
  
  // Redo last undone action
  const redo = useCallback(() => {
    if (redoStackRef.current.length === 0) return;
    
    const action = redoStackRef.current.pop();
    
    // Execute redo (do action again)
    switch (action.type) {
      case ActionTypes.ADD_TRACK:
        setTracks(prev => [...prev, action.track]);
        break;
      case ActionTypes.REMOVE_TRACK:
        setTracks(prev => prev.filter(t => t.id !== action.trackId));
        break;
      case ActionTypes.UPDATE_TRACK:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId ? { ...t, ...action.newValues } : t
        ));
        break;
      case ActionTypes.SET_BPM:
        setBpm(action.newValue);
        break;
      case ActionTypes.SET_MASTER_VOLUME:
        setMasterVolume(action.newValue);
        break;
      case ActionTypes.IMPORT_AUDIO:
      case ActionTypes.RECORD_AUDIO:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, audioBuffer: action.newAudioBuffer, waveformData: action.newWaveformData, duration: action.newDuration }
            : t
        ));
        break;
      case ActionTypes.ADD_INSTRUMENT_TRACK:
        setTracks(prev => [...prev, action.track]);
        break;
      case ActionTypes.REMOVE_INSTRUMENT_TRACK:
        setTracks(prev => prev.filter(t => t.id !== action.trackId));
        break;
      case ActionTypes.RECORD_MIDI:
        // Redo MIDI recording - restore new events
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiEvents: action.newMidiEvents || [], midiClips: action.newMidiClips || [] }
            : t
        ));
        break;
      // MIDI Clip redo actions
      case ActionTypes.ADD_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: [...(t.midiClips || []), action.clip] }
            : t
        ));
        break;
      case ActionTypes.REMOVE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).filter(c => c.id !== action.clipId) }
            : t
        ));
        if (editingClip?.id === action.clipId) {
          setEditingClip(null);
          setShowPianoRoll(false);
        }
        break;
      case ActionTypes.UPDATE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === action.clipId ? action.newClip : c) }
            : t
        ));
        if (editingClip?.id === action.clipId) {
          setEditingClip(action.newClip);
        }
        break;
      case ActionTypes.MOVE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === action.clipId ? { ...c, startTime: action.newStartTime } : c) }
            : t
        ));
        break;
      case ActionTypes.RESIZE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === action.clipId ? { ...c, duration: action.newDuration } : c) }
            : t
        ));
        break;
      case ActionTypes.DUPLICATE_MIDI_CLIP:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: [...(t.midiClips || []), action.newClip] }
            : t
        ));
        break;
      // Piano Roll note redo actions
      case ActionTypes.PIANO_ROLL_ADD_NOTE:
      case ActionTypes.PIANO_ROLL_REMOVE_NOTE:
      case ActionTypes.PIANO_ROLL_MOVE_NOTE:
      case ActionTypes.PIANO_ROLL_RESIZE_NOTE:
      case ActionTypes.PIANO_ROLL_UPDATE_VELOCITY:
      case ActionTypes.PIANO_ROLL_QUANTIZE:
      case ActionTypes.PIANO_ROLL_DELETE_NOTES:
        // Apply the new clip state
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, midiClips: (t.midiClips || []).map(c => c.id === action.clipId ? action.newClip : c) }
            : t
        ));
        if (editingClip?.id === action.clipId) {
          setEditingClip(action.newClip);
        }
        break;
      // Automation redo actions
      case ActionTypes.ADD_AUTOMATION_LANE:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, automationLanes: [...(t.automationLanes || []), action.lane] }
            : t
        ));
        break;
      case ActionTypes.REMOVE_AUTOMATION_LANE:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, automationLanes: (t.automationLanes || []).filter(l => l.id !== action.laneId) }
            : t
        ));
        break;
      case ActionTypes.UPDATE_AUTOMATION_LANE:
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { ...t, automationLanes: (t.automationLanes || []).map(l => l.id === action.laneId ? action.newLane : l) }
            : t
        ));
        break;
      // Clip split/join redo actions
      case ActionTypes.SPLIT_MIDI_CLIP:
        // Redo split: remove original, add split clips
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { 
                ...t, 
                midiClips: [
                  ...(t.midiClips || []).filter(c => c.id !== action.originalClipId),
                  action.leftClip,
                  action.rightClip,
                ]
              }
            : t
        ));
        break;
      case ActionTypes.JOIN_MIDI_CLIPS:
        // Redo join: remove originals, add merged
        setTracks(prev => prev.map(t => 
          t.id === action.trackId 
            ? { 
                ...t, 
                midiClips: [
                  ...(t.midiClips || []).filter(c => !action.originalClipIds.includes(c.id)),
                  action.mergedClip,
                ]
              }
            : t
        ));
        break;
      case ActionTypes.SPLIT_AUDIO_CLIP:
        // Redo audio split: update original track, add right track
        setTracks(prev => {
          const updated = prev.map(t => 
            t.id === action.trackId 
              ? { ...t, ...action.leftTrackUpdates }
              : t
          );
          return [...updated, action.rightTrack];
        });
        break;
      default:
        break;
    }
    
    // Push back to undo stack
    undoStackRef.current.push(action);
    
    // Update state
    setCanUndo(true);
    setCanRedo(redoStackRef.current.length > 0);
    
    toast.info('Redo');
  }, []);
  
  // Clear history (on project load/duplicate)
  const clearHistory = useCallback(() => {
    undoStackRef.current = [];
    redoStackRef.current = [];
    setCanUndo(false);
    setCanRedo(false);
  }, []);
  
  // ============== MIXER HANDLERS ==============
  // Mixer handlers (handleMixerVolumeChange, handleMixerPanChange, handleMixerMuteToggle, 
  // handleMixerSoloToggle, handleMixerNameChange) are provided by useMixer hook above
  
  // ============== EFFECT CHAIN HANDLERS ==============
  
  // Add effect to track
  const handleAddEffect = useCallback((trackId, effect) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        const effectChain = t.effectChain || [];
        return { ...t, effectChain: [...effectChain, effect] };
      }
      return t;
    }));
  }, []);
  
  // Remove effect from track
  const handleRemoveEffect = useCallback((trackId, effectId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId && t.effectChain) {
        return { ...t, effectChain: t.effectChain.filter(e => e.id !== effectId) };
      }
      return t;
    }));
  }, []);
  
  // Toggle effect bypass
  const handleToggleEffect = useCallback((effectId) => {
    setTracks(prev => prev.map(t => {
      if (t.effectChain) {
        const updated = t.effectChain.map(e => 
          e.id === effectId ? { ...e, enabled: !e.enabled } : e
        );
        if (updated !== t.effectChain) {
          return { ...t, effectChain: updated };
        }
      }
      return t;
    }));
  }, []);
  
  // Reorder effects in chain
  const handleReorderEffects = useCallback((trackId, newOrder) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, effectChain: newOrder } : t
    ));
  }, []);
  
  // Update effect parameter
  const handleEffectParamChange = useCallback((effectId, paramName, value) => {
    setTracks(prev => prev.map(t => {
      if (t.effectChain) {
        const updated = t.effectChain.map(e => {
          if (e.id === effectId) {
            return { ...e, params: { ...e.params, [paramName]: value } };
          }
          return e;
        });
        return { ...t, effectChain: updated };
      }
      return t;
    }));
  }, []);
  
  // ============== MASTER PLUGIN CHAIN HANDLERS ==============
  
  // Add plugin to master chain
  const handleAddMasterPlugin = useCallback((pluginName) => {
    if (!audioContextRef.current) return;
    
    try {
      const plugin = pluginRegistry.createInstance(pluginName, audioContextRef.current);
      if (plugin) {
        setMasterPluginChain(prev => [...prev, plugin]);
        masterPluginChainRef.current?.addPlugin(plugin);
        toast.success(`Added ${plugin.name} to master chain`);
      }
    } catch (error) {
      console.error('Failed to create plugin:', error);
      toast.error('Failed to add plugin');
    }
  }, []);
  
  // Remove plugin from master chain
  const handleRemoveMasterPlugin = useCallback((pluginId) => {
    setMasterPluginChain(prev => prev.filter(p => p.id !== pluginId));
    masterPluginChainRef.current?.removePlugin(pluginId);
    pluginRegistry.destroyInstance(pluginId);
  }, []);
  
  // Toggle plugin bypass
  const handleToggleMasterPluginBypass = useCallback((pluginId) => {
    setMasterPluginChain(prev => {
      const plugin = prev.find(p => p.id === pluginId);
      if (plugin) {
        plugin.setEnabled(!plugin.enabled);
      }
      // Return a new array to trigger re-render, but keep the same plugin instances
      return [...prev];
    });
  }, []);
  
  // Reorder plugins in master chain
  const handleReorderMasterPlugins = useCallback((newOrder) => {
    setMasterPluginChain(newOrder);
    // Rebuild plugin chain connections
    if (masterPluginChainRef.current) {
      masterPluginChainRef.current.plugins = newOrder;
      masterPluginChainRef.current._rebuildChain();
    }
  }, []);
  
  // Update plugin parameter
  const handleMasterPluginParamChange = useCallback((pluginId, paramId, value) => {
    setMasterPluginChain(prev => {
      const plugin = prev.find(p => p.id === pluginId);
      if (plugin) {
        plugin.setParameter(paramId, value);
      }
      // Return a new array to trigger re-render, but keep the same plugin instances
      return [...prev];
    });
  }, []);
  
  // Duplicate plugin
  const handleDuplicateMasterPlugin = useCallback((pluginId) => {
    const originalPlugin = masterPluginChain.find(p => p.id === pluginId);
    if (!originalPlugin || !audioContextRef.current) return;
    
    try {
      // Get the plugin class name from registry
      const pluginInfo = pluginRegistry.getRegisteredPlugins().find(
        p => p.name === originalPlugin.name.toLowerCase().replace(' ', '-') ||
             p.metadata?.name === originalPlugin.name
      );
      
      if (pluginInfo) {
        const newPlugin = pluginRegistry.createInstance(pluginInfo.name, audioContextRef.current);
        if (newPlugin) {
          // Copy parameters
          originalPlugin.parameters.forEach((param, id) => {
            newPlugin.setParameter(id, param.getValue());
          });
          
          setMasterPluginChain(prev => [...prev, newPlugin]);
          masterPluginChainRef.current?.addPlugin(newPlugin);
        }
      }
    } catch (error) {
      console.error('Failed to duplicate plugin:', error);
    }
  }, [masterPluginChain]);
  
  // Reset plugin to defaults
  const handleResetMasterPlugin = useCallback((pluginId) => {
    setMasterPluginChain(prev => {
      const plugin = prev.find(p => p.id === pluginId);
      if (plugin) {
        plugin.parameters.forEach(param => param.reset());
      }
      // Return a new array to trigger re-render, but keep the same plugin instances
      return [...prev];
    });
  }, []);
  
  // Load chain preset
  const handleLoadChainPreset = useCallback((preset) => {
    if (!audioContextRef.current || !preset.plugins) return;
    
    // Clear existing plugins
    masterPluginChain.forEach(p => {
      masterPluginChainRef.current?.removePlugin(p.id);
      pluginRegistry.destroyInstance(p.id);
    });
    setMasterPluginChain([]);
    
    // Create new plugins from preset
    const newPlugins = [];
    preset.plugins.forEach(pluginConfig => {
      try {
        const plugin = pluginRegistry.createInstance(pluginConfig.type, audioContextRef.current);
        if (plugin) {
          // Apply preset parameters
          if (pluginConfig.params) {
            Object.entries(pluginConfig.params).forEach(([paramId, value]) => {
              plugin.setParameter(paramId, value);
            });
          }
          newPlugins.push(plugin);
          masterPluginChainRef.current?.addPlugin(plugin);
        }
      } catch (error) {
        console.error(`Failed to create plugin ${pluginConfig.type}:`, error);
      }
    });
    
    setMasterPluginChain(newPlugins);
  }, [masterPluginChain]);
  
  // ============== PER-TRACK PLUGIN CHAIN HANDLERS ==============
  
  // Open plugin browser for a specific track
  const handleOpenTrackPluginBrowser = useCallback((trackId) => {
    setTrackPluginBrowserTarget(trackId);
  }, []);
  
  // Add plugin to a track's chain
  const handleAddTrackPlugin = useCallback((trackId, pluginName) => {
    if (!audioContextRef.current) return;
    
    try {
      const plugin = pluginRegistry.createInstance(pluginName, audioContextRef.current);
      if (plugin) {
        setTracks(prev => prev.map(t => {
          if (t.id === trackId) {
            return {
              ...t,
              pluginChain: [...(t.pluginChain || []), plugin]
            };
          }
          return t;
        }));
        toast.success(`Added ${plugin.name} to track`);
      }
    } catch (error) {
      console.error('Failed to create plugin:', error);
      toast.error('Failed to add plugin');
    }
    
    setTrackPluginBrowserTarget(null);
  }, []);
  
  // Remove plugin from track's chain
  const handleRemoveTrackPlugin = useCallback((trackId, pluginId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        return {
          ...t,
          pluginChain: (t.pluginChain || []).filter(p => p.id !== pluginId)
        };
      }
      return t;
    }));
    pluginRegistry.destroyInstance(pluginId);
  }, []);
  
  // Toggle plugin bypass on a track
  const handleToggleTrackPluginBypass = useCallback((trackId, pluginId) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId && t.pluginChain) {
        return {
          ...t,
          pluginChain: t.pluginChain.map(p => {
            if (p.id === pluginId) {
              p.setEnabled(!p.enabled);
              return { ...p };
            }
            return p;
          })
        };
      }
      return t;
    }));
  }, []);
  
  // Reorder plugins in a track's chain
  const handleReorderTrackPlugins = useCallback((trackId, newOrder) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId) {
        return { ...t, pluginChain: newOrder };
      }
      return t;
    }));
  }, []);
  
  // Update plugin parameter on a track
  const handleTrackPluginParamChange = useCallback((trackId, pluginId, paramId, value) => {
    setTracks(prev => prev.map(t => {
      if (t.id === trackId && t.pluginChain) {
        return {
          ...t,
          pluginChain: t.pluginChain.map(p => {
            if (p.id === pluginId) {
              p.setParameter(paramId, value);
              return { ...p };
            }
            return p;
          })
        };
      }
      return t;
    }));
  }, []);
  
  // Open track preset selector for a specific track
  const handleOpenTrackPresets = useCallback((trackId) => {
    setTrackPresetTarget(trackId);
  }, []);
  
  // Load a preset to a track
  const handleLoadTrackPreset = useCallback((preset, trackId) => {
    if (!audioContextRef.current || !preset?.plugins) return;
    
    try {
      // Destroy existing plugins on the track
      const track = tracks.find(t => t.id === trackId);
      if (track?.pluginChain) {
        track.pluginChain.forEach(p => {
          if (p.id) pluginRegistry.destroyInstance(p.id);
        });
      }
      
      // Create new plugins from preset
      const newPlugins = preset.plugins.map(config => {
        const plugin = pluginRegistry.createInstance(config.type, audioContextRef.current);
        if (plugin && config.params) {
          // Apply preset parameters
          Object.entries(config.params).forEach(([paramId, value]) => {
            try {
              plugin.setParameter(paramId, value);
            } catch (e) {
              // Parameter might not exist, ignore
            }
          });
        }
        return plugin;
      }).filter(Boolean);
      
      // Update the track's plugin chain
      setTracks(prev => prev.map(t => {
        if (t.id === trackId) {
          return { ...t, pluginChain: newPlugins };
        }
        return t;
      }));
      
      setTrackPresetTarget(null);
    } catch (error) {
      console.error('Failed to load track preset:', error);
      toast.error('Failed to load preset');
    }
  }, [tracks]);
  
  // Handle chord wizard insert to track
  const handleChordWizardInsert = useCallback((midiClip, progression) => {
    // Find an instrument track or create one
    let targetTrack = tracks.find(t => t.type === 'instrument' && !t.midiClips?.length);
    
    if (!targetTrack) {
      // Create a new instrument track for the chords
      const newTrackId = `track-${Date.now()}`;
      targetTrack = {
        id: newTrackId,
        name: `Chords - ${progression.key}${progression.scale === 'minor' ? 'm' : ''}`,
        type: 'instrument',
        instrumentType: 'piano',
        volume: 0.8,
        pan: 0,
        muted: false,
        solo: false,
        color: 'bg-purple-500/20 border-purple-500/30',
        midiClips: [],
      };
      
      setTracks(prev => [...prev, targetTrack]);
    }
    
    // Add the MIDI clip to the track
    setTracks(prev => prev.map(t => {
      if (t.id === targetTrack.id) {
        return {
          ...t,
          midiClips: [...(t.midiClips || []), midiClip],
        };
      }
      return t;
    }));
    
    // Set BPM if needed
    if (progression.tempo && progression.tempo !== bpm) {
      setBpm(progression.tempo);
    }
    
    toast.success(`Chord progression added to "${targetTrack.name}"`);
  }, [tracks, bpm]);
  
  // Handle chord wizard add to chord track
  const handleChordWizardAddToChordTrack = useCallback((chordRegions, progression) => {
    // Replace current chord track content with generated progression
    setChordRegions(chordRegions);
    
    // Set BPM if needed
    if (progression.tempo && progression.tempo !== bpm) {
      setBpm(progression.tempo);
    }
    
    // Enable chord track if hidden
    if (!showChordTrack) {
      setShowChordTrack(true);
    }
    
    toast.success(`${chordRegions.length} chords added to Chord Track`);
  }, [bpm, showChordTrack]);
  
  // Handle Quick Start template selection
  const handleQuickStartTemplate = useCallback((template) => {
    // Generate unique track IDs and color classes
    const trackColors = [
      'from-cyan-500 to-cyan-600',
      'from-purple-500 to-purple-600',
      'from-amber-500 to-amber-600',
      'from-emerald-500 to-emerald-600',
      'from-rose-500 to-rose-600',
      'from-indigo-500 to-indigo-600',
      'from-pink-500 to-pink-600',
      'from-orange-500 to-orange-600',
    ];
    
    const newTracks = template.tracks.map((trackTemplate, index) => ({
      id: `track-${Date.now()}-${index}`,
      name: trackTemplate.name,
      type: trackTemplate.type,
      instrumentType: trackTemplate.instrumentType || null,
      volume: 0.8,
      pan: 0,
      muted: false,
      solo: false,
      color: `bg-gradient-to-r ${trackColors[index % trackColors.length]}`,
      eq: { bass: 0, mid: 0, treble: 0 },
      effects: { reverb: 0, delay: 0 },
      startTime: 0,
      duration: 0,
      hasAudio: false,
      midiClips: [],
      automationLanes: [],
    }));
    
    setTracks(newTracks);
    setBpm(template.bpm);
    setProjectName(`New ${template.name}`);
    setChordRegions([]); // Clear chord track
    
    toast.success(`Created ${template.name} with ${template.tracks.length} tracks at ${template.bpm} BPM`);
  }, []);
  
  // ============== TAP TEMPO ==============
  
  const TAP_TEMPO_TIMEOUT = 2000; // Reset after 2 seconds of no taps
  const MIN_TAP_INTERVAL = 200; // Minimum 200ms between taps (300 BPM max)
  const MAX_TAP_INTERVAL = 2000; // Maximum 2000ms between taps (30 BPM min)
  
  const handleTapTempo = useCallback(() => {
    const now = performance.now();
    
    // Clear timeout
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }
    
    setTapTimes(prev => {
      const newTaps = [...prev, now];
      
      // Keep only last 6 taps
      if (newTaps.length > 6) {
        newTaps.shift();
      }
      
      // Calculate BPM from intervals
      if (newTaps.length >= 2) {
        const intervals = [];
        for (let i = 1; i < newTaps.length; i++) {
          const interval = newTaps[i] - newTaps[i - 1];
          // Only use valid intervals
          if (interval >= MIN_TAP_INTERVAL && interval <= MAX_TAP_INTERVAL) {
            intervals.push(interval);
          }
        }
        
        if (intervals.length > 0) {
          const avgInterval = intervals.reduce((a, b) => a + b, 0) / intervals.length;
          const newBpm = Math.round(60000 / avgInterval);
          setCalculatedBpm(Math.max(20, Math.min(300, newBpm)));
        }
      }
      
      return newTaps;
    });
    
    setTapTempoActive(true);
    
    // Auto-reset after timeout
    tapTimeoutRef.current = setTimeout(() => {
      setTapTempoActive(false);
      setTapTimes([]);
      setCalculatedBpm(null);
    }, TAP_TEMPO_TIMEOUT);
  }, []);
  
  // Commit tap tempo BPM
  const commitTapTempo = useCallback(() => {
    if (calculatedBpm) {
      // Push undo action
      pushUndoAction({
        type: ActionTypes.SET_BPM,
        oldValue: bpm,
        newValue: calculatedBpm,
      });
      
      setBpm(calculatedBpm);
      
      // Broadcast BPM change to collaborators
      if (showCollaboration && window.__collabSendBpmChange) {
        window.__collabSendBpmChange(calculatedBpm);
      }
      
      toast.success(`BPM set to ${calculatedBpm}`);
    }
    
    // Reset
    setTapTempoActive(false);
    setTapTimes([]);
    setCalculatedBpm(null);
    if (tapTimeoutRef.current) {
      clearTimeout(tapTimeoutRef.current);
    }
  }, [calculatedBpm, bpm, pushUndoAction, showCollaboration]);
  
  // Session management handlers
  const handleCreateSession = useCallback(async (sessionId) => {
    setCollabSessionId(sessionId);
    setShowCollaboration(true);
    // Persist to localStorage
    try {
      localStorage.setItem('collabSessionId', sessionId);
    } catch (e) {
      console.error('Failed to persist session ID:', e);
    }
  }, []);
  
  const handleJoinSession = useCallback(async (sessionId) => {
    setCollabSessionId(sessionId);
    setShowCollaboration(true);
    // Persist to localStorage
    try {
      localStorage.setItem('collabSessionId', sessionId);
    } catch (e) {
      console.error('Failed to persist session ID:', e);
    }
  }, []);
  
  const handleLeaveSession = useCallback(() => {
    setShowCollaboration(false);
    setCollabSessionId(null);
    setCollabUsers([]);
    setRemoteCursors({});
    // Remove from localStorage
    try {
      localStorage.removeItem('collabSessionId');
    } catch (e) {
      console.error('Failed to remove session ID:', e);
    }
  }, []);
  
  // Store initial session ID on mount for auto-reconnect
  const initialSessionIdRef = useRef(collabSessionId);
  
  // Auto-reconnect to persisted session on mount only
  useEffect(() => {
    const sessionId = initialSessionIdRef.current;
    if (sessionId) {
      // Check if session still exists before auto-connecting
      setShowCollaboration(true);
      toast.info(`Reconnecting to session: ${sessionId}`);
    }
  }, []); // Dependencies are intentionally empty - uses ref for initial value
  
  // Generate waveform data from audio buffer - using utility from ./utils
  const generateWaveform = generateWaveformUtil;
  
  // Add new track (with undo support and subscription check)
  const addTrack = useCallback(() => {
    // Check subscription-based track limit
    if (tracks.length >= effectiveMaxTracks) {
      if (subscriptionTrackLimit !== -1 && subscriptionTrackLimit < MAX_TRACKS) {
        toast.error(
          <div className="flex items-center gap-2">
            <span>Track limit reached. Upgrade for more tracks!</span>
            <button 
              onClick={() => navigate('/pricing')}
              className="px-2 py-1 bg-amber-500 text-black text-xs rounded font-medium"
            >
              Upgrade
            </button>
          </div>,
          { duration: 5000 }
        );
      }
      return;
    }
    
    const newTrack = createEmptyTrack(tracks.length);
    
    // Push undo action
    pushUndoAction({
      type: ActionTypes.ADD_TRACK,
      trackId: newTrack.id,
      track: newTrack,
    });
    
    setTracks(prev => [...prev, newTrack]);
  }, [tracks.length, effectiveMaxTracks, subscriptionTrackLimit, navigate, pushUndoAction]);
  
  // Import multiple tracks (batch import with subscription check)
  const handleBatchImportTracks = useCallback((importedTracks) => {
    if (!importedTracks || importedTracks.length === 0) return;
    
    // Check if we have space for new tracks (using subscription limit)
    const availableSlots = effectiveMaxTracks - tracks.length;
    
    if (availableSlots <= 0) {
      toast.error(
        <div className="flex items-center gap-2">
          <span>Track limit reached. Upgrade for more tracks!</span>
          <button 
            onClick={() => navigate('/pricing')}
            className="px-2 py-1 bg-amber-500 text-black text-xs rounded font-medium"
          >
            Upgrade
          </button>
        </div>,
        { duration: 5000 }
      );
      return;
    }
    
    const tracksToAdd = importedTracks.slice(0, availableSlots);
    
    if (tracksToAdd.length < importedTracks.length) {
      toast.warning(`Only ${tracksToAdd.length} of ${importedTracks.length} tracks imported (max ${effectiveMaxTracks} tracks on your plan)`);
    }
    
    // Add each track with undo support
    tracksToAdd.forEach(track => {
      pushUndoAction({
        type: ActionTypes.ADD_TRACK,
        trackId: track.id,
        track: track,
      });
    });
    
    setTracks(prev => [...prev, ...tracksToAdd]);
    setSelectedTrack(tracksToAdd[0]?.id || tracks[0]?.id);
  }, [tracks.length, tracks, effectiveMaxTracks, navigate, pushUndoAction]);
  
  // Remove track (with undo support)
  const removeTrack = useCallback((trackId) => {
    if (tracks.length <= 1) return;
    
    const trackToRemove = tracks.find(t => t.id === trackId);
    if (!trackToRemove) return;
    
    // Push undo action
    pushUndoAction({
      type: ActionTypes.REMOVE_TRACK,
      trackId,
      track: { ...trackToRemove },
    });
    
    setTracks(prev => prev.filter(t => t.id !== trackId));
  }, [tracks, pushUndoAction]);
  
  // Duplicate track function
  const duplicateTrack = useCallback((trackId) => {
    // Check subscription-based track limit
    if (tracks.length >= effectiveMaxTracks) {
      if (subscriptionTrackLimit !== -1 && subscriptionTrackLimit < MAX_TRACKS) {
        toast.error(
          <div className="flex items-center gap-2">
            <span>Track limit reached. Upgrade for more tracks!</span>
            <button 
              onClick={() => navigate('/pricing')}
              className="px-2 py-1 bg-amber-500 text-black text-xs rounded font-medium"
            >
              Upgrade
            </button>
          </div>,
          { duration: 5000 }
        );
      }
      return;
    }
    
    const trackToDuplicate = tracks.find(t => t.id === trackId);
    if (!trackToDuplicate) return;
    
    // Create a new track with duplicated settings
    const newTrackId = tracks.length;
    const duplicatedTrack = {
      ...trackToDuplicate,
      id: newTrackId,
      name: `${trackToDuplicate.name} (Copy)`,
      color: TRACK_COLORS[newTrackId % TRACK_COLORS.length],
      // Copy audio buffer if exists (creates a reference, not a deep copy - fine for playback)
      audioBuffer: trackToDuplicate.audioBuffer,
      waveformData: [...(trackToDuplicate.waveformData || [])],
      // Copy EQ settings
      eq: { ...trackToDuplicate.eq },
      // Copy effects settings
      effects: { ...trackToDuplicate.effects },
      // Copy guitar effects
      guitarEffects: trackToDuplicate.guitarEffects ? { ...trackToDuplicate.guitarEffects } : null,
      // Copy plugin chain
      pluginChain: trackToDuplicate.pluginChain ? trackToDuplicate.pluginChain.map(p => ({ ...p })) : [],
      // Copy automation lanes
      automationLanes: trackToDuplicate.automationLanes ? trackToDuplicate.automationLanes.map(lane => ({
        ...lane,
        id: `${lane.id}_copy_${Date.now()}`,
        points: lane.points.map(p => ({ ...p, id: `${p.id}_copy_${Date.now()}` }))
      })) : [],
      // Copy MIDI data if exists
      midiClips: trackToDuplicate.midiClips ? trackToDuplicate.midiClips.map(clip => ({
        ...clip,
        id: `${clip.id}_copy_${Date.now()}`
      })) : [],
    };
    
    // Push undo action
    pushUndoAction({
      type: ActionTypes.ADD_TRACK,
      trackId: duplicatedTrack.id,
      track: duplicatedTrack,
    });
    
    setTracks(prev => [...prev, duplicatedTrack]);
    toast.success(`Track "${trackToDuplicate.name}" duplicated`);
  }, [tracks, effectiveMaxTracks, subscriptionTrackLimit, navigate, pushUndoAction]);
  
  // Update track property (with optional undo support)
  const updateTrack = useCallback((trackId, updates, skipUndo = false) => {
    if (!skipUndo) {
      const track = tracks.find(t => t.id === trackId);
      if (track) {
        // Get old values for undo
        const oldValues = {};
        Object.keys(updates).forEach(key => {
          oldValues[key] = track[key];
        });
        
        // Push undo action
        pushUndoAction({
          type: ActionTypes.UPDATE_TRACK,
          trackId,
          oldValues,
          newValues: updates,
        });
      }
    }
    
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, ...updates } : t
    ));
  }, [tracks, pushUndoAction]);
  
  // Handle guitar effects changes for a track
  const handleGuitarEffectsChange = useCallback((trackId, guitarEffects) => {
    // Update track state
    setTracks(prev => prev.map(t => 
      t.id === trackId ? { ...t, guitarEffects } : t
    ));
    
    // Update audio chain if context exists
    if (audioContextRef.current) {
      let chain = guitarEffectChainsRef.current[trackId];
      
      if (guitarEffects?.enabled) {
        // Create chain if it doesn't exist
        if (!chain) {
          chain = new GuitarEffectsChain(audioContextRef.current);
          guitarEffectChainsRef.current[trackId] = chain;
        }
        
        // Enable and configure
        chain.enable();
        
        // Configure amp
        if (guitarEffects.ampEnabled) {
          chain.setAmpEnabled(true, {
            ampModel: guitarEffects.ampModel,
            cabinet: guitarEffects.cabinet,
            controls: guitarEffects.ampControls,
          });
        } else {
          chain.setAmpEnabled(false);
        }
        
        // Configure pedals
        if (guitarEffects.pedals) {
          chain.setPedals(guitarEffects.pedals);
        }
      } else if (chain) {
        // Disable chain
        chain.disable();
      }
    }
  }, []);

  // ============== MIDI / INSTRUMENT FUNCTIONS ==============
  
  // Add instrument track (with subscription check)
  const addInstrumentTrack = useCallback((instrumentType = 'synth') => {
    // Check subscription-based track limit
    if (tracks.length >= effectiveMaxTracks) {
      if (subscriptionTrackLimit !== -1 && subscriptionTrackLimit < MAX_TRACKS) {
        toast.error(
          <div className="flex items-center gap-2">
            <span>Track limit reached. Upgrade for more tracks!</span>
            <button 
              onClick={() => navigate('/pricing')}
              className="px-2 py-1 bg-amber-500 text-black text-xs rounded font-medium"
            >
              Upgrade
            </button>
          </div>,
          { duration: 5000 }
        );
      } else {
        toast.error('Maximum tracks reached');
      }
      return;
    }
    
    // Check if MIDI tools are available (Pro+ feature)
    const hasMidiTools = getFeatureLimit('midi_tools');
    if (!hasMidiTools) {
      toast.error(
        <div className="flex items-center gap-2">
          <span>MIDI tracks require Pro subscription!</span>
          <button 
            onClick={() => navigate('/pricing')}
            className="px-2 py-1 bg-amber-500 text-black text-xs rounded font-medium"
          >
            Upgrade
          </button>
        </div>,
        { duration: 5000 }
      );
      return;
    }
    
    const newTrack = createInstrumentTrack(tracks.length, null, instrumentType);
    
    // Push undo action
    pushUndoAction({
      type: ActionTypes.ADD_INSTRUMENT_TRACK,
      trackId: newTrack.id,
      track: newTrack,
    });
    
    setTracks(prev => [...prev, newTrack]);
    setSelectedTrack(newTrack.id);
    toast.success(`${instrumentType === 'drums' ? 'Drum' : 'Synth'} track added`);
  }, [tracks.length, effectiveMaxTracks, subscriptionTrackLimit, getFeatureLimit, navigate, pushUndoAction]);
  

  // ============== MIDI HANDLERS ==============
  // Note: MIDI note input, recording, and clip operations are now handled by useMIDI hook
  // See destructured functions: handleMidiNoteOn, handleMidiNoteOff, handleDrumHit,
  // startMidiRecording, stopMidiRecording, openClipInPianoRoll, closePianoRoll,
  // handlePianoRollClipUpdate, handleMoveClip, handleResizeClip, handleDeleteClip,
  // handleDuplicateClip, createEmptyMidiClip, handleSplitMidiClip, handleJoinMidiClips

  // Split audio clip at a specific time
  const handleSplitAudioClip = useCallback(async (trackId, splitTime) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track || !track.audioBuffer) {
      toast.error('No audio to split');
      return;
    }
    
    // Ensure split time is within audio bounds
    if (splitTime <= track.startTime || splitTime >= track.startTime + track.duration) {
      toast.error('Cannot split at this position');
      return;
    }
    
    const ctx = audioContextRef.current;
    if (!ctx) return;
    
    const originalBuffer = track.audioBuffer;
    const sampleRate = originalBuffer.sampleRate;
    const channels = originalBuffer.numberOfChannels;
    
    // Calculate sample positions
    const relativeTime = splitTime - track.startTime;
    const splitSample = Math.floor(relativeTime * sampleRate);
    const leftLength = splitSample;
    const rightLength = originalBuffer.length - splitSample;
    
    // Create left audio buffer
    const leftBuffer = ctx.createBuffer(channels, leftLength, sampleRate);
    for (let ch = 0; ch < channels; ch++) {
      const sourceData = originalBuffer.getChannelData(ch);
      const leftData = leftBuffer.getChannelData(ch);
      for (let i = 0; i < leftLength; i++) {
        leftData[i] = sourceData[i];
      }
    }
    
    // Create right audio buffer
    const rightBuffer = ctx.createBuffer(channels, rightLength, sampleRate);
    for (let ch = 0; ch < channels; ch++) {
      const sourceData = originalBuffer.getChannelData(ch);
      const rightData = rightBuffer.getChannelData(ch);
      for (let i = 0; i < rightLength; i++) {
        rightData[i] = sourceData[splitSample + i];
      }
    }
    
    // Store original track state for undo
    const originalTrackState = {
      audioBuffer: track.audioBuffer,
      waveformData: track.waveformData,
      startTime: track.startTime,
      duration: track.duration,
    };
    
    // Create new track for the right part
    const newTrackId = Math.max(...tracks.map(t => t.id)) + 1;
    const rightTrack = {
      ...createEmptyTrack(newTrackId, `${track.name} (R)`),
      audioBuffer: rightBuffer,
      waveformData: generateWaveform(rightBuffer),
      startTime: splitTime,
      duration: rightBuffer.duration,
      volume: track.volume,
      pan: track.pan,
      muted: track.muted,
      eq: { ...track.eq },
      effects: { ...track.effects },
    };
    
    // Update the left track
    const leftTrackUpdates = {
      audioBuffer: leftBuffer,
      waveformData: generateWaveform(leftBuffer),
      duration: leftBuffer.duration,
      name: `${track.name} (L)`,
    };
    
    pushUndoAction({
      type: ActionTypes.SPLIT_AUDIO_CLIP,
      trackId,
      originalTrackState,
      leftTrackUpdates,
      rightTrack,
      newTrackId,
    });
    
    setTracks(prev => {
      const updated = prev.map(t => 
        t.id === trackId 
          ? { ...t, ...leftTrackUpdates }
          : t
      );
      return [...updated, rightTrack];
    });
    
    toast.success('Audio split into two tracks');
  }, [tracks, pushUndoAction]);
  
  // ============== WAVEFORM EDITING HELPERS ==============
  
  // Helper: Generate waveform data from audio buffer - using utility from ./utils
  const generateWaveformFromBuffer = useCallback((buffer, samples = 200) => {
    return generateWaveformPeaks(buffer, samples);
  }, []);
  
  // ============== WAVEFORM EDITING HANDLERS ==============
  
  // Handle fade change on audio track
  const handleFadeChange = useCallback((trackId, { fadeIn, fadeOut }) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    const oldFadeIn = track.fadeIn || 0;
    const oldFadeOut = track.fadeOut || 0;
    
    pushUndoAction({
      type: ActionTypes.UPDATE_TRACK,
      trackId,
      oldValues: { fadeIn: oldFadeIn, fadeOut: oldFadeOut },
      newValues: { fadeIn, fadeOut },
    });
    
    setTracks(prev => prev.map(t => 
      t.id === trackId 
        ? { ...t, fadeIn, fadeOut }
        : t
    ));
    
    toast.success(`Fade updated`);
  }, [tracks, pushUndoAction]);
  
  // Handle loop point change on audio track
  const handleLoopPointsChange = useCallback((trackId, { loopStart: clipLoopStart, loopEnd: clipLoopEnd }) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    const oldLoopStart = track.clipLoopStart ?? null;
    const oldLoopEnd = track.clipLoopEnd ?? null;
    
    pushUndoAction({
      type: ActionTypes.UPDATE_TRACK,
      trackId,
      oldValues: { clipLoopStart: oldLoopStart, clipLoopEnd: oldLoopEnd },
      newValues: { clipLoopStart, clipLoopEnd },
    });
    
    setTracks(prev => prev.map(t => 
      t.id === trackId 
        ? { ...t, clipLoopStart, clipLoopEnd }
        : t
    ));
    
    toast.success('Loop points set');
  }, [tracks, pushUndoAction]);
  
  // Handle trim change on audio track
  const handleTrimChange = useCallback((trackId, { trimStart, trimEnd, newDuration }) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track || !track.audioBuffer) return;
    
    const audioBuffer = track.audioBuffer;
    const ctx = audioContextRef.current;
    if (!ctx) return;
    
    // Store original duration if not already stored
    const originalDuration = track.originalDuration || audioBuffer.duration;
    
    // Calculate new duration based on trim values
    const calculatedDuration = originalDuration - trimStart - trimEnd;
    
    if (calculatedDuration < 0.1) {
      toast.error('Clip too short');
      return;
    }
    
    const oldTrimStart = track.trimStart || 0;
    const oldTrimEnd = track.trimEnd || 0;
    const oldDuration = track.duration;
    const oldStartTime = track.startTime;
    
    // Calculate new start time (trimming from start shifts the clip position)
    const newStartTime = track.startTime + (trimStart - oldTrimStart);
    
    // Create a new trimmed audio buffer
    const sampleRate = audioBuffer.sampleRate;
    const startSample = Math.floor(trimStart * sampleRate);
    const endSample = Math.floor((originalDuration - trimEnd) * sampleRate);
    const newLength = endSample - startSample;
    
    if (newLength <= 0) {
      toast.error('Invalid trim');
      return;
    }
    
    // Create trimmed buffer
    const trimmedBuffer = ctx.createBuffer(
      audioBuffer.numberOfChannels,
      newLength,
      sampleRate
    );
    
    // Copy trimmed data - need to get original untrimmed data
    const sourceBuffer = track.originalAudioBuffer || audioBuffer;
    for (let channel = 0; channel < sourceBuffer.numberOfChannels; channel++) {
      const sourceData = sourceBuffer.getChannelData(channel);
      const destData = trimmedBuffer.getChannelData(channel);
      for (let i = 0; i < newLength; i++) {
        destData[i] = sourceData[startSample + i] || 0;
      }
    }
    
    // Generate new waveform data
    const newWaveformData = generateWaveformFromBuffer(trimmedBuffer);
    
    pushUndoAction({
      type: ActionTypes.UPDATE_TRACK,
      trackId,
      oldValues: { 
        trimStart: oldTrimStart, 
        trimEnd: oldTrimEnd, 
        duration: oldDuration,
        startTime: oldStartTime,
        audioBuffer: track.audioBuffer,
        waveformData: track.waveformData,
      },
      newValues: { 
        trimStart, 
        trimEnd, 
        duration: calculatedDuration,
        startTime: newStartTime,
        audioBuffer: trimmedBuffer,
        waveformData: newWaveformData,
        originalDuration,
        originalAudioBuffer: track.originalAudioBuffer || audioBuffer,
      },
    });
    
    setTracks(prev => prev.map(t => 
      t.id === trackId 
        ? { 
            ...t, 
            trimStart, 
            trimEnd, 
            duration: calculatedDuration,
            startTime: newStartTime,
            audioBuffer: trimmedBuffer,
            waveformData: newWaveformData,
            originalDuration,
            originalAudioBuffer: t.originalAudioBuffer || audioBuffer,
          }
        : t
    ));
    
    toast.success(`Trimmed: ${calculatedDuration.toFixed(2)}s`);
  }, [tracks, pushUndoAction, generateWaveformFromBuffer]);
  
  // Select audio clip for editing
  const handleSelectAudioClip = useCallback((trackId) => {
    setSelectedAudioClip(prev => 
      prev?.trackId === trackId ? null : { trackId }
    );
    setWaveformEditMode(true);
  }, []);
  
  // Deselect audio clip
  const handleDeselectAudioClip = useCallback(() => {
    setSelectedAudioClip(null);
    setWaveformEditMode(false);
    setAudioSelection(null);
  }, []);
  
  // Handle audio selection change from WaveformCanvas
  const handleAudioSelectionChange = useCallback((trackId, selection) => {
    if (selection && selection.start !== selection.end) {
      setAudioSelection({ trackId, ...selection });
    } else {
      setAudioSelection(null);
    }
  }, []);
  
  // ============== CUT/COPY/PASTE HANDLERS ==============
  
  // Copy selected audio region to clipboard
  const handleCopyAudio = useCallback(() => {
    if (!audioSelection) {
      toast.error('No audio region selected');
      return;
    }
    
    const track = tracks.find(t => t.id === audioSelection.trackId);
    if (!track || !track.audioBuffer) {
      toast.error('No audio to copy');
      return;
    }
    
    const { start, end } = audioSelection;
    const audioBuffer = track.audioBuffer;
    const sampleRate = audioBuffer.sampleRate;
    const startSample = Math.floor(start * sampleRate);
    const endSample = Math.floor(end * sampleRate);
    const length = endSample - startSample;
    
    if (length <= 0) {
      toast.error('Invalid selection');
      return;
    }
    
    // Create a new buffer with the selected region
    const ctx = audioContextRef.current;
    const newBuffer = ctx.createBuffer(
      audioBuffer.numberOfChannels,
      length,
      sampleRate
    );
    
    // Copy channel data
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const sourceData = audioBuffer.getChannelData(channel);
      const destData = newBuffer.getChannelData(channel);
      for (let i = 0; i < length; i++) {
        destData[i] = sourceData[startSample + i];
      }
    }
    
    setAudioClipboard({
      buffer: newBuffer,
      duration: end - start,
      sourceTrackId: track.id,
    });
    
    toast.success(`Copied ${(end - start).toFixed(2)}s of audio`);
  }, [audioSelection, tracks]);
  
  // Cut selected audio region (copy + delete)
  const handleCutAudio = useCallback(() => {
    if (!audioSelection) {
      toast.error('No audio region selected');
      return;
    }
    
    const track = tracks.find(t => t.id === audioSelection.trackId);
    if (!track || !track.audioBuffer) {
      toast.error('No audio to cut');
      return;
    }
    
    // First copy to clipboard
    handleCopyAudio();
    
    const { start, end } = audioSelection;
    const audioBuffer = track.audioBuffer;
    const sampleRate = audioBuffer.sampleRate;
    const startSample = Math.floor(start * sampleRate);
    const endSample = Math.floor(end * sampleRate);
    
    // Create new buffer without the cut region
    const originalLength = audioBuffer.length;
    const cutLength = endSample - startSample;
    const newLength = originalLength - cutLength;
    
    if (newLength <= 0) {
      // Cutting everything - remove audio from track
      const oldAudioBuffer = track.audioBuffer;
      const oldWaveformData = track.waveformData;
      const oldDuration = track.duration;
      
      pushUndoAction({
        type: ActionTypes.UPDATE_TRACK,
        trackId: track.id,
        oldValues: { audioBuffer: oldAudioBuffer, waveformData: oldWaveformData, duration: oldDuration, hasAudio: true },
        newValues: { audioBuffer: null, waveformData: [], duration: 0, hasAudio: false },
      });
      
      setTracks(prev => prev.map(t => 
        t.id === track.id 
          ? { ...t, audioBuffer: null, waveformData: [], duration: 0, hasAudio: false }
          : t
      ));
      
      setAudioSelection(null);
      toast.success('Audio region cut');
      return;
    }
    
    // Create new buffer
    const ctx = audioContextRef.current;
    const newBuffer = ctx.createBuffer(
      audioBuffer.numberOfChannels,
      newLength,
      sampleRate
    );
    
    // Copy data before and after cut region
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const sourceData = audioBuffer.getChannelData(channel);
      const destData = newBuffer.getChannelData(channel);
      
      // Copy before cut
      for (let i = 0; i < startSample; i++) {
        destData[i] = sourceData[i];
      }
      
      // Copy after cut
      for (let i = endSample; i < originalLength; i++) {
        destData[i - cutLength] = sourceData[i];
      }
    }
    
    // Generate new waveform data
    const newWaveformData = generateWaveformFromBuffer(newBuffer);
    
    const oldAudioBuffer = track.audioBuffer;
    const oldWaveformData = track.waveformData;
    const oldDuration = track.duration;
    
    pushUndoAction({
      type: ActionTypes.UPDATE_TRACK,
      trackId: track.id,
      oldValues: { audioBuffer: oldAudioBuffer, waveformData: oldWaveformData, duration: oldDuration },
      newValues: { audioBuffer: newBuffer, waveformData: newWaveformData, duration: newBuffer.duration },
    });
    
    setTracks(prev => prev.map(t => 
      t.id === track.id 
        ? { ...t, audioBuffer: newBuffer, waveformData: newWaveformData, duration: newBuffer.duration }
        : t
    ));
    
    setAudioSelection(null);
    toast.success('Audio region cut');
  }, [audioSelection, tracks, handleCopyAudio, pushUndoAction]);
  
  // Paste audio from clipboard at current time
  const handlePasteAudio = useCallback(() => {
    if (!audioClipboard) {
      toast.error('Nothing to paste');
      return;
    }
    
    // Paste into selected track or source track
    const targetTrackId = selectedAudioClip?.trackId || audioClipboard.sourceTrackId;
    const track = tracks.find(t => t.id === targetTrackId);
    
    if (!track) {
      toast.error('No target track selected');
      return;
    }
    
    const clipboardBuffer = audioClipboard.buffer;
    const insertTime = currentTime - track.startTime; // Insert at playhead position relative to track start
    
    if (!track.audioBuffer) {
      // No existing audio - just set the clipboard as the track audio
      const newWaveformData = generateWaveformFromBuffer(clipboardBuffer);
      
      pushUndoAction({
        type: ActionTypes.UPDATE_TRACK,
        trackId: track.id,
        oldValues: { audioBuffer: null, waveformData: [], duration: 0, hasAudio: false },
        newValues: { audioBuffer: clipboardBuffer, waveformData: newWaveformData, duration: clipboardBuffer.duration, hasAudio: true },
      });
      
      setTracks(prev => prev.map(t => 
        t.id === track.id 
          ? { ...t, audioBuffer: clipboardBuffer, waveformData: newWaveformData, duration: clipboardBuffer.duration, hasAudio: true }
          : t
      ));
      
      toast.success('Audio pasted');
      return;
    }
    
    // Insert clipboard audio into existing audio at playhead position
    const existingBuffer = track.audioBuffer;
    const sampleRate = existingBuffer.sampleRate;
    const insertSample = Math.max(0, Math.floor(insertTime * sampleRate));
    const clipboardLength = clipboardBuffer.length;
    const newLength = existingBuffer.length + clipboardLength;
    
    const ctx = audioContextRef.current;
    const newBuffer = ctx.createBuffer(
      existingBuffer.numberOfChannels,
      newLength,
      sampleRate
    );
    
    // Copy data: before insert + clipboard + after insert
    for (let channel = 0; channel < existingBuffer.numberOfChannels; channel++) {
      const sourceData = existingBuffer.getChannelData(channel);
      const clipData = clipboardBuffer.numberOfChannels > channel 
        ? clipboardBuffer.getChannelData(channel) 
        : clipboardBuffer.getChannelData(0);
      const destData = newBuffer.getChannelData(channel);
      
      // Copy before insert point
      for (let i = 0; i < insertSample && i < existingBuffer.length; i++) {
        destData[i] = sourceData[i];
      }
      
      // Insert clipboard
      for (let i = 0; i < clipboardLength; i++) {
        destData[insertSample + i] = clipData[i];
      }
      
      // Copy after insert point
      for (let i = insertSample; i < existingBuffer.length; i++) {
        destData[clipboardLength + i] = sourceData[i];
      }
    }
    
    const newWaveformData = generateWaveformFromBuffer(newBuffer);
    const oldAudioBuffer = track.audioBuffer;
    const oldWaveformData = track.waveformData;
    const oldDuration = track.duration;
    
    pushUndoAction({
      type: ActionTypes.UPDATE_TRACK,
      trackId: track.id,
      oldValues: { audioBuffer: oldAudioBuffer, waveformData: oldWaveformData, duration: oldDuration },
      newValues: { audioBuffer: newBuffer, waveformData: newWaveformData, duration: newBuffer.duration },
    });
    
    setTracks(prev => prev.map(t => 
      t.id === track.id 
        ? { ...t, audioBuffer: newBuffer, waveformData: newWaveformData, duration: newBuffer.duration }
        : t
    ));
    
    toast.success(`Pasted ${audioClipboard.duration.toFixed(2)}s of audio`);
  }, [audioClipboard, selectedAudioClip, tracks, currentTime, pushUndoAction]);
  
  // Delete selected audio region
  const handleDeleteAudioSelection = useCallback(() => {
    if (!audioSelection) {
      toast.error('No audio region selected');
      return;
    }
    
    const track = tracks.find(t => t.id === audioSelection.trackId);
    if (!track || !track.audioBuffer) {
      toast.error('No audio to delete');
      return;
    }
    
    const { start, end } = audioSelection;
    const audioBuffer = track.audioBuffer;
    const sampleRate = audioBuffer.sampleRate;
    const startSample = Math.floor(start * sampleRate);
    const endSample = Math.floor(end * sampleRate);
    
    const originalLength = audioBuffer.length;
    const deleteLength = endSample - startSample;
    const newLength = originalLength - deleteLength;
    
    if (newLength <= 0) {
      // Deleting everything
      const oldAudioBuffer = track.audioBuffer;
      const oldWaveformData = track.waveformData;
      const oldDuration = track.duration;
      
      pushUndoAction({
        type: ActionTypes.UPDATE_TRACK,
        trackId: track.id,
        oldValues: { audioBuffer: oldAudioBuffer, waveformData: oldWaveformData, duration: oldDuration, hasAudio: true },
        newValues: { audioBuffer: null, waveformData: [], duration: 0, hasAudio: false },
      });
      
      setTracks(prev => prev.map(t => 
        t.id === track.id 
          ? { ...t, audioBuffer: null, waveformData: [], duration: 0, hasAudio: false }
          : t
      ));
      
      setAudioSelection(null);
      toast.success('Audio deleted');
      return;
    }
    
    // Create new buffer without deleted region
    const ctx = audioContextRef.current;
    const newBuffer = ctx.createBuffer(
      audioBuffer.numberOfChannels,
      newLength,
      sampleRate
    );
    
    for (let channel = 0; channel < audioBuffer.numberOfChannels; channel++) {
      const sourceData = audioBuffer.getChannelData(channel);
      const destData = newBuffer.getChannelData(channel);
      
      for (let i = 0; i < startSample; i++) {
        destData[i] = sourceData[i];
      }
      
      for (let i = endSample; i < originalLength; i++) {
        destData[i - deleteLength] = sourceData[i];
      }
    }
    
    const newWaveformData = generateWaveformFromBuffer(newBuffer);
    const oldAudioBuffer = track.audioBuffer;
    const oldWaveformData = track.waveformData;
    const oldDuration = track.duration;
    
    pushUndoAction({
      type: ActionTypes.UPDATE_TRACK,
      trackId: track.id,
      oldValues: { audioBuffer: oldAudioBuffer, waveformData: oldWaveformData, duration: oldDuration },
      newValues: { audioBuffer: newBuffer, waveformData: newWaveformData, duration: newBuffer.duration },
    });
    
    setTracks(prev => prev.map(t => 
      t.id === track.id 
        ? { ...t, audioBuffer: newBuffer, waveformData: newWaveformData, duration: newBuffer.duration }
        : t
    ));
    
    setAudioSelection(null);
    toast.success('Audio region deleted');
  }, [audioSelection, tracks, pushUndoAction]);
  
  // ============== KEYBOARD SHORTCUTS FOR CLIPBOARD OPERATIONS ==============
  
  // Keyboard shortcuts for undo/redo and audio clipboard operations
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Skip if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }
      
      // Cmd/Ctrl + Z = Undo
      // Cmd/Ctrl + Shift + Z = Redo
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        return;
      }
      
      // Cmd/Ctrl + X = Cut audio selection
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'x') {
        if (audioSelection) {
          e.preventDefault();
          handleCutAudio();
        }
        return;
      }
      
      // Cmd/Ctrl + C = Copy audio selection
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
        if (audioSelection) {
          e.preventDefault();
          handleCopyAudio();
        }
        return;
      }
      
      // Cmd/Ctrl + V = Paste audio from clipboard
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'v') {
        if (audioClipboard) {
          e.preventDefault();
          handlePasteAudio();
        }
        return;
      }
      
      // Delete or Backspace = Delete audio selection
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (audioSelection) {
          e.preventDefault();
          handleDeleteAudioSelection();
        }
        return;
      }
      
      // Cmd/Ctrl + 1 = Edit View
      if ((e.metaKey || e.ctrlKey) && e.key === '1') {
        e.preventDefault();
        setViewMode('edit');
        return;
      }
      
      // Cmd/Ctrl + 2 = Mix View
      if ((e.metaKey || e.ctrlKey) && e.key === '2') {
        e.preventDefault();
        setViewMode('mix');
        return;
      }
      
      // ? = Show keyboard shortcuts
      if (e.key === '?' || (e.shiftKey && e.key === '/')) {
        e.preventDefault();
        setShowKeyboardShortcuts(prev => !prev);
        return;
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undo, redo, audioSelection, audioClipboard, handleCutAudio, handleCopyAudio, handlePasteAudio, handleDeleteAudioSelection]);
  
  // ============== AUTOMATION HANDLERS ==============
  
  // Add automation lane to track
  const addAutomationLane = useCallback((trackId, parameter = 'volume') => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    // Get param config - supports both track and plugin parameters
    const paramConfig = getParamConfig(parameter) || AUTOMATION_PARAMS[parameter];
    if (!paramConfig) {
      toast.error('Unknown parameter type');
      return;
    }
    
    const newLane = createAutomationLane({
      trackId,
      parameter,
      points: [
        createAutomationPoint({ time: 0, value: paramConfig.default }),
      ],
    });
    
    pushUndoAction({
      type: ActionTypes.ADD_AUTOMATION_LANE,
      trackId,
      laneId: newLane.id,
      lane: newLane,
    });
    
    setTracks(prev => prev.map(t => 
      t.id === trackId 
        ? { ...t, automationLanes: [...(t.automationLanes || []), newLane] }
        : t
    ));
    
    setShowAutomation(true);
    
    // Determine display label for toast
    const isPluginParam = parameter.includes(':');
    const displayLabel = isPluginParam 
      ? paramConfig.label 
      : paramConfig.label;
    toast.success(`${displayLabel} automation added`);
  }, [tracks, pushUndoAction]);
  
  // Delete automation lane
  const deleteAutomationLane = useCallback((trackId, laneId) => {
    const track = tracks.find(t => t.id === trackId);
    if (!track) return;
    
    const lane = track.automationLanes?.find(l => l.id === laneId);
    if (!lane) return;
    
    pushUndoAction({
      type: ActionTypes.REMOVE_AUTOMATION_LANE,
      trackId,
      laneId,
      lane,
    });
    
    setTracks(prev => prev.map(t => 
      t.id === trackId 
        ? { ...t, automationLanes: (t.automationLanes || []).filter(l => l.id !== laneId) }
        : t
    ));
  }, [tracks, pushUndoAction]);
  
  // Update automation lane
  const updateAutomationLane = useCallback((trackId, updatedLane, action) => {
    if (action?.skipUndo) {
      // Just update without pushing to undo stack (for dragging)
      setTracks(prev => prev.map(t => 
        t.id === trackId 
          ? { ...t, automationLanes: (t.automationLanes || []).map(l => l.id === updatedLane.id ? updatedLane : l) }
          : t
      ));
      return;
    }
    
    // Use functional update to get latest state and push undo action
    setTracks(prev => {
      const track = prev.find(t => t.id === trackId);
      const oldLane = track?.automationLanes?.find(l => l.id === updatedLane.id);
      
      if (action && oldLane) {
        pushUndoAction({
          type: ActionTypes.UPDATE_AUTOMATION_LANE,
          trackId,
          laneId: updatedLane.id,
          oldLane: { ...oldLane }, // Create a copy to preserve the old state
          newLane: updatedLane,
          actionType: action.type,
        });
      }
      
      return prev.map(t => 
        t.id === trackId 
          ? { ...t, automationLanes: (t.automationLanes || []).map(l => l.id === updatedLane.id ? updatedLane : l) }
          : t
      );
    });
  }, [pushUndoAction]);
  
  // Toggle automation lane visibility
  const toggleAutomationLaneVisibility = useCallback((trackId, laneId) => {
    setTracks(prev => prev.map(t => 
      t.id === trackId 
        ? { 
            ...t, 
            automationLanes: (t.automationLanes || []).map(l => 
              l.id === laneId ? { ...l, isVisible: !l.isVisible } : l
            ) 
          }
        : t
    ));
  }, []);
  
  // Apply automation values during playback
  const applyAutomationAtTime = useCallback((time) => {
    tracks.forEach(track => {
      if (!track.automationLanes || track.automationLanes.length === 0) return;
      
      track.automationLanes.forEach(lane => {
        if (!lane.isVisible) return;
        
        // Get param config - supports both track and plugin parameters
        const paramConfig = getParamConfig(lane.parameter) || AUTOMATION_PARAMS[lane.parameter];
        if (!paramConfig) return;
        
        const value = getValueAtTime(lane, time, paramConfig);
        
        // Check if this is a plugin parameter
        if (lane.parameter.includes(':')) {
          // Plugin parameter automation
          const parsed = parseFullPluginParamKey(lane.parameter);
          if (!parsed.isPlugin) return;
          
          // Find the plugin in the track's plugin chain
          const plugin = track.pluginChain?.find(p => p.id === parsed.pluginId);
          if (!plugin) return;
          
          // Update the plugin parameter
          // This updates the state - the actual audio processing happens
          // when the plugin chain processes audio
          if (plugin.params && parsed.paramName in plugin.params) {
            plugin.params[parsed.paramName] = value;
          }
          
          // Also update via the plugin's setParameter method if available
          if (plugin.setParameter) {
            plugin.setParameter(parsed.paramName, value);
          }
          
          // Notify the track that its plugin chain has been updated
          // This is handled through React state updates when automation changes
          return;
        }
        
        // Track-level parameter automation
        const gainNode = trackGainNodesRef.current[track.id];
        if (!gainNode) return;
        
        // Apply the automation value
        switch (lane.parameter) {
          case 'volume':
            if (gainNode.gain) {
              gainNode.gain.value = value;
            }
            break;
          case 'pan':
            // Pan would require a StereoPannerNode
            // TODO: Implement pan automation
            break;
          case 'eqBass':
          case 'eqMid':
          case 'eqTreble':
            // EQ would require BiquadFilterNodes
            // TODO: Implement EQ automation
            break;
          case 'reverb':
          case 'delay':
            // Effects would require effect nodes
            // TODO: Implement effect automation
            break;
          default:
            break;
        }
      });
    });
  }, [tracks]);

  // Handle file import (with undo support)
  const handleFileImport = async (trackId, file) => {
    if (!file || !audioContextRef.current) return;
    
    try {
      const track = tracks.find(t => t.id === trackId);
      const oldAudioBuffer = track?.audioBuffer || null;
      const oldWaveformData = track?.waveformData || [];
      const oldDuration = track?.duration || 0;
      
      const arrayBuffer = await file.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      const waveformData = generateWaveform(audioBuffer);
      
      // Push undo action
      pushUndoAction({
        type: ActionTypes.IMPORT_AUDIO,
        trackId,
        oldAudioBuffer,
        oldWaveformData,
        oldDuration,
        newAudioBuffer: audioBuffer,
        newWaveformData: waveformData,
        newDuration: audioBuffer.duration,
      });
      
      updateTrack(trackId, {
        audioBuffer,
        waveformData,
        duration: audioBuffer.duration,
        name: file.name.replace(/\.[^/.]+$/, ''),
      }, true); // Skip undo for the update since we already tracked it
      
      toast.success('Audio imported!');
    } catch (error) {
      console.error('Error importing audio:', error);
      toast.error('Failed to import audio file');
    }
  };
  
  // Handle sample drop from Sample Library (P2 feature)
  const handleSampleDrop = useCallback(async ({ sample, trackId, startTime }) => {
    if (!sample || !audioContextRef.current) return;
    
    try {
      // Fetch the sample audio from the backend
      const response = await fetch(`${API_URL}/api/samples/audio/${sample.id}`);
      if (!response.ok) {
        throw new Error('Failed to fetch sample audio');
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
      
      // Find the target track
      const track = tracks.find(t => t.id === trackId);
      if (!track) {
        toast.error('Target track not found');
        return;
      }
      
      // Store old values for undo
      const oldAudioBuffer = track.audioBuffer;
      const oldWaveformData = track.waveformData || [];
      const oldDuration = track.duration || 0;
      const oldStartTime = track.startTime || 0;
      
      // Generate waveform for the sample
      const waveformData = generateWaveform(audioBuffer);
      
      // Create undo action
      pushUndoAction({
        type: ActionTypes.IMPORT_AUDIO,
        trackId,
        oldAudioBuffer,
        oldWaveformData,
        oldDuration,
        newAudioBuffer: audioBuffer,
        newWaveformData: waveformData,
        newDuration: audioBuffer.duration,
      });
      
      // Update the track with the sample
      updateTrack(trackId, {
        audioBuffer,
        waveformData,
        duration: audioBuffer.duration,
        startTime: startTime,
        name: sample.name,
      }, true);
      
      toast.success(`Added "${sample.name}" to track`);
    } catch (error) {
      console.error('Error dropping sample:', error);
      toast.error('Failed to add sample to track');
    }
  }, [tracks, pushUndoAction, updateTrack]);
  
  // Start recording
  const startRecording = async (trackId) => {
    try {
      // Resume audio context if suspended
      if (!audioContextRef.current) {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)({
          sampleRate: 44100,
          latencyHint: 'playback',
        });
      }
      if (audioContextRef.current.state === 'suspended') {
        await audioContextRef.current.resume();
      }
      
      // REUSE the existing live monitoring stream if available.
      // Creating a second getUserMedia for the same device causes Chrome
      // to mute/interrupt the first stream, killing live monitoring volume.
      let stream = liveMonitorStreamRef.current;
      let createdNewStream = false;
      
      if (!stream || stream.getTracks().every(t => t.readyState === 'ended')) {
        // No live monitoring stream — create one for recording
        const audioConstraints = {
          audio: {
            ...(selectedAudioInput ? { deviceId: { exact: selectedAudioInput } } : {}),
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
            channelCount: 2,
            sampleRate: 44100,
          }
        };
        stream = await navigator.mediaDevices.getUserMedia(audioConstraints);
        createdNewStream = true;
      }
      
      // If count-in is enabled, do the count-in first
      if (countInEnabled && countInBeats > 0) {
        setIsCountingIn(true);
        setCountInProgress(0);
        await performCountIn();
        setIsCountingIn(false);
        setCountInProgress(0);
      }
      
      // Start MediaRecorder on the shared stream (records raw audio)
      const recorderOptions = {
        mimeType: MediaRecorder.isTypeSupported('audio/webm;codecs=opus') 
          ? 'audio/webm;codecs=opus' 
          : 'audio/webm'
      };
      
      mediaRecorderRef.current = new MediaRecorder(stream, recorderOptions);
      recordedChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (e) => {
        if (e.data.size > 0) {
          recordedChunksRef.current.push(e.data);
        }
      };
      
      // Keep a ref so we know if we need to clean up this stream on stop
      mediaRecorderRef.current._createdNewStream = createdNewStream;
      mediaRecorderRef.current._stream = stream;
      
      mediaRecorderRef.current.start(1000);
      
      mediaRecorderRef.current.onstop = async () => {
        // Only clean up if we created a new stream for recording
        // Don't touch live monitoring stream - it stays active
        if (mediaRecorderRef.current?._createdNewStream && mediaRecorderRef.current?._stream) {
          mediaRecorderRef.current._stream.getTracks().forEach(t => t.stop());
        }
        
        const blob = new Blob(recordedChunksRef.current, { type: 'audio/webm' });
        const arrayBuffer = await blob.arrayBuffer();
        
        try {
          const track = tracks.find(t => t.id === trackId);
          const oldAudioBuffer = track?.audioBuffer || null;
          const oldWaveformData = track?.waveformData || [];
          const oldDuration = track?.duration || 0;
          
          const audioBuffer = await audioContextRef.current.decodeAudioData(arrayBuffer);
          const waveformData = generateWaveform(audioBuffer);
          
          // Push undo action for recording
          pushUndoAction({
            type: ActionTypes.RECORD_AUDIO,
            trackId,
            oldAudioBuffer,
            oldWaveformData,
            oldDuration,
            newAudioBuffer: audioBuffer,
            newWaveformData: waveformData,
            newDuration: audioBuffer.duration,
          });
          
          updateTrack(trackId, {
            audioBuffer,
            waveformData,
            duration: audioBuffer.duration,
          }, true); // Skip undo since we already tracked it
          
          toast.success('Recording saved!');
        } catch (error) {
          console.error('Error processing recording:', error);
          toast.error('Failed to process recording');
        }
        
        // Stop playback of other tracks
        stopAll();
        setIsRecording(false);
        setRecordingTrack(null);
      };
      
      // Recording is already started above with timeslice
      setIsRecording(true);
      setRecordingTrack(trackId);
      
      // AUTO-PLAY other tracks for overdubbing
      // Play back all existing tracks (except the one being recorded) so user can play along
      // Muted tracks are automatically skipped by playAll
      const hasAudioTracks = tracks.some(t => t.id !== trackId && t.audioBuffer);
      if (hasAudioTracks) {
        playAll(trackId); // Exclude the recording track
      }
      
      toast.info('Recording started — play along!');
    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Could not access microphone. Please check permissions.');
    }
  };
  
  // Perform count-in before recording
  const performCountIn = () => {
    return new Promise((resolve) => {
      const intervalMs = (60 / bpm) * 1000;
      let beatCount = 0;
      
      // Play first beat immediately
      playMetronomeClick(true);
      setCountInProgress(1);
      beatCount++;
      
      if (countInBeats === 1) {
        resolve();
        return;
      }
      
      const interval = setInterval(() => {
        beatCount++;
        const isAccent = beatCount % timeSignature === 1;
        playMetronomeClick(isAccent);
        setCountInProgress(beatCount);
        
        if (beatCount >= countInBeats) {
          clearInterval(interval);
          resolve();
        }
      }, intervalMs);
    });
  };
  
  // Update performCountInRef so hooks can use it
  useEffect(() => {
    performCountInRef.current = performCountIn;
  });
  
  // Stop recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
    }
  };
  
  // ============== MIDI CLIP PLAYBACK ==============
  
  // Schedule all MIDI notes from clips for playback
  const scheduleMidiClipPlayback = useCallback((startFromTime = 0) => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    const audioStartTime = ctx.currentTime;
    const hasSolo = tracks.some(t => t.solo);
    
    // Clear any previously scheduled events
    scheduledMidiEventsRef.current.forEach(event => {
      clearTimeout(event.noteOnTimeout);
      clearTimeout(event.noteOffTimeout);
    });
    scheduledMidiEventsRef.current = [];
    
    // Initialize active notes tracking for each track
    activeMidiNotesRef.current = new Map();
    
    tracks.forEach(track => {
      // Only process instrument tracks with MIDI clips
      if (track.type !== 'instrument') return;
      if (!track.midiClips || track.midiClips.length === 0) return;
      if (track.muted) return;
      if (hasSolo && !track.solo) return;
      
      // Initialize active notes set for this track
      activeMidiNotesRef.current.set(track.id, new Set());
      
      // Get the appropriate synth for this track
      const synth = track.instrumentType === 'drums' ? drumSynthRef.current : synthRef.current;
      if (!synth) return;
      
      track.midiClips.forEach(clip => {
        if (!clip.notes || clip.notes.length === 0) return;
        
        clip.notes.forEach(note => {
          // Calculate absolute note start time in the project timeline
          const absoluteNoteStart = clip.startTime + note.startTime;
          const absoluteNoteEnd = absoluteNoteStart + note.duration;
          
          // Skip notes that have already finished by startFromTime
          if (absoluteNoteEnd <= startFromTime) return;
          
          // Calculate when to trigger relative to now
          const noteOnDelay = Math.max(0, absoluteNoteStart - startFromTime);
          const noteOffDelay = Math.max(0, absoluteNoteEnd - startFromTime);
          
          // For notes already in progress at startFromTime, play immediately
          const isNoteInProgress = absoluteNoteStart < startFromTime && absoluteNoteEnd > startFromTime;
          
          // Schedule note on
          const noteOnTimeout = setTimeout(() => {
            if (!audioContextRef.current) return;
            
            // Track this note as active
            const activeNotes = activeMidiNotesRef.current.get(track.id);
            if (activeNotes) activeNotes.add(note.pitch);
            
            // Play the note
            if (track.instrumentType === 'drums') {
              // Find drum name from note pitch
              const drumEntry = Object.entries(DRUM_KIT).find(([_, d]) => d.note === note.pitch);
              if (drumEntry) {
                synth.playDrum(drumEntry[0], note.velocity);
              }
            } else {
              synth.noteOn(note.pitch, note.velocity);
            }
          }, isNoteInProgress ? 0 : noteOnDelay * 1000);
          
          // Schedule note off (only for synth, drums are one-shot)
          let noteOffTimeout = null;
          if (track.instrumentType !== 'drums') {
            const adjustedNoteOffDelay = isNoteInProgress 
              ? (absoluteNoteEnd - startFromTime) * 1000 
              : noteOffDelay * 1000;
              
            noteOffTimeout = setTimeout(() => {
              if (!audioContextRef.current) return;
              
              // Remove from active notes
              const activeNotes = activeMidiNotesRef.current.get(track.id);
              if (activeNotes) activeNotes.delete(note.pitch);
              
              // Stop the note
              synth.noteOff(note.pitch);
            }, adjustedNoteOffDelay);
          }
          
          // Store the scheduled event for cleanup
          scheduledMidiEventsRef.current.push({
            noteOnTimeout,
            noteOffTimeout,
            trackId: track.id,
            pitch: note.pitch,
          });
        });
      });
    });
  }, [tracks]);
  
  // Stop all MIDI playback and release active notes
  const stopMidiPlayback = useCallback(() => {
    // Clear all scheduled events
    scheduledMidiEventsRef.current.forEach(event => {
      clearTimeout(event.noteOnTimeout);
      if (event.noteOffTimeout) clearTimeout(event.noteOffTimeout);
    });
    scheduledMidiEventsRef.current = [];
    
    // Release all active notes
    if (synthRef.current) {
      synthRef.current.stopAll();
    }
    
    // Clear active notes tracking
    activeMidiNotesRef.current = new Map();
  }, []);
  
  // Play all tracks (optionally exclude a track, e.g. during overdub recording)
  const playAll = async (excludeTrackId = null) => {
    if (!audioContextRef.current) return;
    
    // Resume context if suspended
    if (audioContextRef.current.state === 'suspended') {
      await audioContextRef.current.resume();
    }
    
    // Stop any existing playback (don't broadcast - we're about to broadcast play)
    stopAll(false);
    
    // Broadcast transport state to collaborators
    if (showCollaboration && window.__collabSendTransport) {
      window.__collabSendTransport('play', currentTime);
    }
    
    const ctx = audioContextRef.current;
    const startTime = ctx.currentTime;
    
    // If loop is enabled and current position is outside loop region, start at loop start
    let playFromTime = currentTime;
    if (loopEnabled) {
      if (currentTime < loopStart || currentTime >= loopEnd) {
        playFromTime = loopStart;
        setCurrentTime(loopStart);
      }
    }
    
    playbackStartTimeRef.current = startTime - playFromTime;
    
    // Check which tracks should play (considering solo)
    const hasSolo = tracks.some(t => t.solo);
    
    // Schedule MIDI clip playback
    scheduleMidiClipPlayback(playFromTime);
    
    tracks.forEach(track => {
      if (!track.audioBuffer) return;
      if (track.muted) return;
      if (hasSolo && !track.solo) return;
      if (track.id === excludeTrackId) return; // Skip track being recorded on
      
      // Create source
      const source = ctx.createBufferSource();
      source.buffer = track.audioBuffer;
      
      // Create gain for volume
      const gainNode = ctx.createGain();
      gainNode.gain.value = track.volume;
      
      // Create gain for fade envelope
      const fadeGainNode = ctx.createGain();
      
      // Schedule fade in/out envelope
      const fadeIn = track.fadeIn || 0;
      const fadeOut = track.fadeOut || 0;
      const trackDuration = track.duration || track.audioBuffer.duration;
      
      // Calculate when this track starts relative to playback
      const trackStartsAt = Math.max(0, track.startTime - playFromTime);
      const offsetIntoTrack = Math.max(0, playFromTime - track.startTime);
      
      // Fade in envelope
      if (fadeIn > 0 && offsetIntoTrack < fadeIn) {
        // We're starting within the fade in region
        const fadeProgress = offsetIntoTrack / fadeIn;
        fadeGainNode.gain.setValueAtTime(fadeProgress, startTime + trackStartsAt);
        const remainingFadeIn = fadeIn - offsetIntoTrack;
        fadeGainNode.gain.linearRampToValueAtTime(1.0, startTime + trackStartsAt + remainingFadeIn);
      } else if (fadeIn > 0) {
        // We're starting after fade in, but still schedule full fade for restarts
        fadeGainNode.gain.setValueAtTime(0, startTime + trackStartsAt);
        fadeGainNode.gain.linearRampToValueAtTime(1.0, startTime + trackStartsAt + fadeIn);
      } else {
        fadeGainNode.gain.setValueAtTime(1.0, startTime + trackStartsAt);
      }
      
      // Fade out envelope
      if (fadeOut > 0) {
        const fadeOutStart = trackDuration - fadeOut;
        const fadeOutStartRelative = Math.max(0, fadeOutStart - offsetIntoTrack);
        fadeGainNode.gain.setValueAtTime(1.0, startTime + trackStartsAt + fadeOutStartRelative);
        fadeGainNode.gain.linearRampToValueAtTime(0, startTime + trackStartsAt + fadeOutStartRelative + fadeOut);
      }
      
      // Create panner
      const pannerNode = ctx.createStereoPanner();
      pannerNode.pan.value = track.pan;
      
      // Create EQ nodes
      const bassEQ = ctx.createBiquadFilter();
      bassEQ.type = 'lowshelf';
      bassEQ.frequency.value = 320;
      bassEQ.gain.value = track.eq.bass * 12;
      
      const midEQ = ctx.createBiquadFilter();
      midEQ.type = 'peaking';
      midEQ.frequency.value = 1000;
      midEQ.Q.value = 0.5;
      midEQ.gain.value = track.eq.mid * 12;
      
      const trebleEQ = ctx.createBiquadFilter();
      trebleEQ.type = 'highshelf';
      trebleEQ.frequency.value = 3200;
      trebleEQ.gain.value = track.eq.treble * 12;
      
      // Connect chain: source -> fadeGain -> volumeGain -> panner -> EQ -> [guitarEffects] -> master
      source.connect(fadeGainNode);
      fadeGainNode.connect(gainNode);
      gainNode.connect(pannerNode);
      pannerNode.connect(bassEQ);
      bassEQ.connect(midEQ);
      midEQ.connect(trebleEQ);
      
      // Insert guitar effects if enabled for this track
      let finalNode = trebleEQ;
      if (track.guitarEffects?.enabled) {
        let guitarChain = guitarEffectChainsRef.current[track.id];
        if (!guitarChain) {
          guitarChain = new GuitarEffectsChain(ctx);
          guitarEffectChainsRef.current[track.id] = guitarChain;
        }
        
        // Enable and configure
        guitarChain.enable();
        
        if (track.guitarEffects.ampEnabled) {
          guitarChain.setAmpEnabled(true, {
            ampModel: track.guitarEffects.ampModel,
            cabinet: track.guitarEffects.cabinet,
            controls: track.guitarEffects.ampControls,
          });
        }
        
        if (track.guitarEffects.pedals) {
          guitarChain.setPedals(track.guitarEffects.pedals);
        }
        
        // Connect through guitar effects
        trebleEQ.connect(guitarChain.input);
        guitarChain.connect(masterGainRef.current);
        finalNode = guitarChain;
      } else {
        // Direct to master
        trebleEQ.connect(masterGainRef.current);
      }
      
      // Calculate offset - use playFromTime to respect loop start position
      const offset = Math.max(0, playFromTime - track.startTime);
      const when = Math.max(0, track.startTime - playFromTime);
      
      source.start(startTime + when, offset);
      sourceNodesRef.current[track.id] = source;
      
      trackNodesRef.current[track.id] = { gainNode, pannerNode, fadeGainNode };
      trackGainNodesRef.current[track.id] = gainNode; // For automation
    });
    
    setIsPlaying(true);
    
    // Update playhead and apply automation
    const updatePlayhead = () => {
      if (!audioContextRef.current) return;
      
      let elapsed = audioContextRef.current.currentTime - playbackStartTimeRef.current;
      
      // Handle loop logic (using refs to get current values)
      if (loopEnabledRef.current && elapsed >= loopEndRef.current) {
        // Calculate how far past loop end we are
        const loopDuration = loopEndRef.current - loopStartRef.current;
        if (loopDuration > 0) {
          // Jump back to loop start
          const overshoot = elapsed - loopEndRef.current;
          const loopPosition = loopStartRef.current + (overshoot % loopDuration);
          
          // Reset playback start time to simulate being at loop position
          playbackStartTimeRef.current = audioContextRef.current.currentTime - loopPosition;
          elapsed = loopPosition;
          
          // Re-schedule audio and MIDI for the new position
          restartPlaybackAtPosition(loopPosition);
        }
      }
      
      setCurrentTime(elapsed);
      
      // Apply automation values at current time
      applyAutomationAtTime(elapsed);
      
      if (elapsed < totalDuration) {
        animationFrameRef.current = requestAnimationFrame(updatePlayhead);
      } else {
        stopAll();
        setCurrentTime(0);
      }
    };
    
    animationFrameRef.current = requestAnimationFrame(updatePlayhead);
  };
  
  // Helper to restart playback at a specific position (for looping)
  const restartPlaybackAtPosition = useCallback((position) => {
    if (!audioContextRef.current) return;
    
    const ctx = audioContextRef.current;
    const startTime = ctx.currentTime;
    
    // Stop current audio sources
    Object.values(sourceNodesRef.current).forEach(source => {
      try { source.stop(); } catch (e) { /* Already stopped */ }
    });
    sourceNodesRef.current = {};
    
    // Stop current MIDI playback
    stopMidiPlayback();
    
    // Re-schedule audio from new position
    const hasSolo = tracks.some(t => t.solo);
    
    tracks.forEach(track => {
      if (!track.audioBuffer) return;
      if (track.muted) return;
      if (hasSolo && !track.solo) return;
      
      // Only play if audio overlaps with current position
      const audioEnd = track.startTime + track.audioBuffer.duration;
      if (position >= audioEnd || position < track.startTime) return;
      
      const source = ctx.createBufferSource();
      source.buffer = track.audioBuffer;
      
      const gainNode = ctx.createGain();
      gainNode.gain.value = track.volume;
      
      const pannerNode = ctx.createStereoPanner();
      pannerNode.pan.value = track.pan;
      
      const bassEQ = ctx.createBiquadFilter();
      bassEQ.type = 'lowshelf';
      bassEQ.frequency.value = 320;
      bassEQ.gain.value = track.eq.bass * 12;
      
      const midEQ = ctx.createBiquadFilter();
      midEQ.type = 'peaking';
      midEQ.frequency.value = 1000;
      midEQ.Q.value = 0.5;
      midEQ.gain.value = track.eq.mid * 12;
      
      const trebleEQ = ctx.createBiquadFilter();
      trebleEQ.type = 'highshelf';
      trebleEQ.frequency.value = 3200;
      trebleEQ.gain.value = track.eq.treble * 12;
      
      source.connect(gainNode);
      gainNode.connect(pannerNode);
      pannerNode.connect(bassEQ);
      bassEQ.connect(midEQ);
      midEQ.connect(trebleEQ);
      trebleEQ.connect(masterGainRef.current);
      
      const offset = Math.max(0, position - track.startTime);
      source.start(startTime, offset);
      sourceNodesRef.current[track.id] = source;
      
      trackNodesRef.current[track.id] = { gainNode, pannerNode };
      trackGainNodesRef.current[track.id] = gainNode;
    });
    
    // Re-schedule MIDI from new position
    scheduleMidiClipPlayback(position);
  }, [tracks, stopMidiPlayback, scheduleMidiClipPlayback]);
  
  // Stop all playback
  const stopAll = (broadcastTransport = true) => {
    // Broadcast transport state to collaborators (only if this is a user-initiated action)
    if (broadcastTransport && isPlaying && showCollaboration && window.__collabSendTransport) {
      window.__collabSendTransport('pause', currentTime);
    }
    
    // Stop audio sources
    Object.values(sourceNodesRef.current).forEach(source => {
      try {
        source.stop();
      } catch (e) {
        // Already stopped
      }
    });
    sourceNodesRef.current = {};
    
    // Stop MIDI playback
    stopMidiPlayback();
    
    if (animationFrameRef.current) {
      cancelAnimationFrame(animationFrameRef.current);
    }
    
    // Stop audio analysis
    if (analysisIntervalRef.current) {
      clearInterval(analysisIntervalRef.current);
      analysisIntervalRef.current = null;
    }
    
    setIsPlaying(false);
  };
  
  // ============== REAL-TIME AUDIO ANALYSIS FOR AI MASTERING ==============
  
  // Initialize audio analyzer for real-time characteristics capture
  const initializeAudioAnalyzer = useCallback(() => {
    if (!audioContextRef.current || audioAnalyzerRef.current) return;
    
    const ctx = audioContextRef.current;
    
    // Create analyzer node
    const analyzer = ctx.createAnalyser();
    analyzer.fftSize = 4096;
    analyzer.smoothingTimeConstant = 0.8;
    
    // Connect to master output
    masterGainRef.current.connect(analyzer);
    audioAnalyzerRef.current = analyzer;
    
    return analyzer;
  }, []);
  
  // Analyze audio characteristics in real-time
  const analyzeAudioCharacteristics = useCallback(() => {
    if (!audioAnalyzerRef.current || !isPlaying) return null;
    
    const analyzer = audioAnalyzerRef.current;
    const bufferLength = analyzer.frequencyBinCount;
    const dataArray = new Float32Array(bufferLength);
    const frequencyData = new Float32Array(bufferLength);
    
    // Get time domain data for RMS/peak
    analyzer.getFloatTimeDomainData(dataArray);
    
    // Get frequency data
    analyzer.getFloatFrequencyData(frequencyData);
    
    // Calculate RMS
    let rmsSum = 0;
    let peak = 0;
    for (let i = 0; i < bufferLength; i++) {
      const sample = dataArray[i];
      rmsSum += sample * sample;
      peak = Math.max(peak, Math.abs(sample));
    }
    const rms = Math.sqrt(rmsSum / bufferLength);
    const rmsDb = 20 * Math.log10(rms + 0.0001);
    const peakDb = 20 * Math.log10(peak + 0.0001);
    
    // Estimate LUFS from RMS (simplified - real LUFS requires K-weighting)
    const lufsEstimate = rmsDb - 0.691; // Approximate adjustment
    
    // Calculate frequency band energy
    const sampleRate = audioContextRef.current?.sampleRate || 44100;
    const binSize = sampleRate / analyzer.fftSize;
    
    const getEnergyInRange = (lowFreq, highFreq) => {
      const lowBin = Math.floor(lowFreq / binSize);
      const highBin = Math.min(Math.floor(highFreq / binSize), bufferLength - 1);
      let energy = 0;
      for (let i = lowBin; i <= highBin; i++) {
        // Convert from dB to linear, then sum
        energy += Math.pow(10, frequencyData[i] / 20);
      }
      return energy;
    };
    
    const subBass = getEnergyInRange(20, 60);
    const bass = getEnergyInRange(60, 250);
    const lowMids = getEnergyInRange(250, 500);
    const mids = getEnergyInRange(500, 2000);
    const highMids = getEnergyInRange(2000, 4000);
    const highs = getEnergyInRange(4000, 10000);
    const air = getEnergyInRange(10000, 20000);
    
    const totalEnergy = subBass + bass + lowMids + mids + highMids + highs + air + 0.0001;
    
    // Calculate stereo metrics (simplified for mono analyzer)
    const stereoWidth = 0.8 + Math.random() * 0.2; // Placeholder - would need stereo splitter
    const stereoCorrelation = 0.7 + Math.random() * 0.2;
    const monoCompatibility = 75 + Math.random() * 15;
    
    // Calculate dynamic range and crest factor
    const dynamicRange = Math.max(0, peakDb - rmsDb);
    const crestFactor = peak / (rms + 0.0001);
    
    const characteristics = {
      // Loudness
      lufs_integrated: lufsEstimate,
      lufs_short_term: lufsEstimate + (Math.random() - 0.5) * 2,
      lufs_momentary: lufsEstimate + (Math.random() - 0.5) * 4,
      true_peak: peakDb,
      loudness_range: Math.max(0, dynamicRange - 5),
      
      // Frequency balance (as percentages)
      sub_bass: (subBass / totalEnergy) * 100,
      bass: (bass / totalEnergy) * 100,
      low_mids: (lowMids / totalEnergy) * 100,
      mids: (mids / totalEnergy) * 100,
      high_mids: (highMids / totalEnergy) * 100,
      highs: (highs / totalEnergy) * 100,
      air: (air / totalEnergy) * 100,
      
      // Dynamics
      dynamic_range: dynamicRange,
      crest_factor: crestFactor,
      rms_level: rmsDb,
      
      // Stereo
      stereo_width: stereoWidth,
      stereo_correlation: stereoCorrelation,
      mono_compatibility: monoCompatibility,
      phase_coherence: 0.9 + Math.random() * 0.1,
    };
    
    return characteristics;
  }, [isPlaying]);
  
  // Start continuous audio analysis during playback
  const startAudioAnalysis = useCallback(() => {
    if (!audioContextRef.current) return;
    
    // Initialize analyzer if needed
    if (!audioAnalyzerRef.current) {
      initializeAudioAnalyzer();
    }
    
    // Clear any existing interval
    if (analysisIntervalRef.current) {
      clearInterval(analysisIntervalRef.current);
    }
    
    // Analyze every 500ms during playback
    analysisIntervalRef.current = setInterval(() => {
      if (isPlaying) {
        const characteristics = analyzeAudioCharacteristics();
        if (characteristics) {
          setAudioCharacteristics(prev => {
            // Smooth the values with previous readings
            if (!prev) return characteristics;
            const smoothed = {};
            for (const key in characteristics) {
              if (typeof characteristics[key] === 'number') {
                smoothed[key] = prev[key] * 0.7 + characteristics[key] * 0.3;
              } else {
                smoothed[key] = characteristics[key];
              }
            }
            return smoothed;
          });
        }
      }
    }, 500);
  }, [isPlaying, initializeAudioAnalyzer, analyzeAudioCharacteristics]);
  
  // Start analysis when playback begins
  useEffect(() => {
    if (isPlaying) {
      startAudioAnalysis();
    }
    return () => {
      if (analysisIntervalRef.current) {
        clearInterval(analysisIntervalRef.current);
        analysisIntervalRef.current = null;
      }
    };
  }, [isPlaying, startAudioAnalysis]);
  
  // Handle applying AI mastering suggestion
  const handleApplyMasteringSuggestion = useCallback((suggestion) => {
    if (!suggestion) return;
    
    const sType = suggestion.type || '';
    const sVal = suggestion.suggestedValue ?? suggestion.suggested_value ?? null;
    const trackId = suggestion.trackId;
    
    switch (sType) {
      case 'volume': {
        if (trackId && trackId !== 'master') {
          // Apply to specific track
          const targetTrack = tracks.find(t => String(t.id) === String(trackId));
          if (targetTrack && sVal !== null) {
            const newVol = Math.max(0, Math.min(1.5, sVal));
            updateTrack(targetTrack.id, { volume: newVol });
            toast.success(`${suggestion.trackName || 'Track'}: volume set to ${(newVol * 100).toFixed(0)}%`);
          }
        } else {
          // Master volume
          if (sVal !== null) {
            const newVol = Math.max(0, Math.min(1.5, sVal));
            setMasterVolume(newVol);
            toast.success(`Master volume set to ${(newVol * 100).toFixed(0)}%`);
          }
        }
        break;
      }
      
      case 'pan': {
        if (trackId) {
          const targetTrack = tracks.find(t => String(t.id) === String(trackId));
          if (targetTrack && sVal !== null) {
            const newPan = Math.max(-1, Math.min(1, sVal));
            updateTrack(targetTrack.id, { pan: newPan });
            const panLabel = newPan === 0 ? 'Center' : newPan < 0 ? `${Math.round(Math.abs(newPan) * 100)}% Left` : `${Math.round(newPan * 100)}% Right`;
            toast.success(`${suggestion.trackName || 'Track'}: panned to ${panLabel}`);
          }
        }
        break;
      }
      
      case 'eq': {
        if (trackId) {
          const targetTrack = tracks.find(t => String(t.id) === String(trackId));
          if (targetTrack && suggestion.parameter) {
            const param = suggestion.parameter.toLowerCase();
            const currentEq = targetTrack.eq || { bass: 0, mid: 0, treble: 0 };
            const newEq = { ...currentEq };
            if (param.includes('bass') || param.includes('low')) newEq.bass = sVal ?? currentEq.bass;
            else if (param.includes('mid')) newEq.mid = sVal ?? currentEq.mid;
            else if (param.includes('treble') || param.includes('high')) newEq.treble = sVal ?? currentEq.treble;
            updateTrack(targetTrack.id, { eq: newEq });
            toast.success(`${suggestion.trackName || 'Track'}: EQ adjusted (${suggestion.parameter})`);
          }
        } else {
          toast.success('EQ suggestion noted — apply EQ adjustments in the track effects chain');
        }
        break;
      }
      
      case 'effect': {
        toast.success(`Effect suggestion: ${suggestion.reason || 'Check the effects chain'}`);
        break;
      }
      
      default:
        toast.info(`Applied: ${suggestion.reason || suggestion.description || 'Suggestion applied'}`);
    }
  }, [tracks, updateTrack, setMasterVolume]);

  // Quick Mix - One-click AI analysis + auto-apply all suggestions
  const [isQuickMixing, setIsQuickMixing] = useState(false);
  const [showQuickMixPro, setShowQuickMixPro] = useState(false);
  const [mixPreferences, setMixPreferences] = useState(null);
  
  // Load Quick Mix Pro preferences on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_URL}/api/ai-mixing/preferences?user_id=${user?.id || 'default'}`);
        const data = await res.json();
        if (data.preferences) setMixPreferences(data.preferences);
      } catch {}
    })();
  }, [API_URL, user?.id]);
  
  const handleQuickMix = useCallback(async () => {
    if (isQuickMixing) return;
    
    const activeTracks = tracks.filter(t => t.audioBuffer || (t.midiClips && t.midiClips.length > 0));
    if (activeTracks.length === 0) {
      toast.error('Add some tracks with audio before running Quick Mix');
      return;
    }
    
    setIsQuickMixing(true);
    const mixToast = toast.loading('Analyzing your mix...');
    
    try {
      const trackData = tracks.map(t => ({
        id: String(t.id),
        name: t.name,
        type: t.type || 'audio',
        volume: t.volume,
        pan: t.pan,
        muted: t.muted || false,
        solo: t.solo || false,
        hasAudio: !!(t.audioBuffer || (t.midiClips && t.midiClips.length > 0)),
      }));
      
      const response = await fetch(`${API_URL}/api/ai-mixing/analyze`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: projectId || 'quick-mix',
          projectName: projectName || 'Untitled',
          bpm,
          genre: mixPreferences?.genre || 'general',
          tracks: trackData,
          masterVolume,
          analysisType: 'general',
          preferences: mixPreferences || undefined,
        }),
      });
      
      if (!response.ok) throw new Error('Analysis failed');
      const data = await response.json();
      
      const suggestions = data.suggestions || [];
      let appliedCount = 0;
      
      // Apply each suggestion silently
      suggestions.forEach(suggestion => {
        const sType = suggestion.type || '';
        const sVal = suggestion.suggestedValue ?? null;
        const trackId = suggestion.trackId;
        
        if (sType === 'volume') {
          if (trackId && trackId !== 'master') {
            const target = tracks.find(t => String(t.id) === String(trackId));
            if (target && sVal !== null) {
              updateTrack(target.id, { volume: Math.max(0, Math.min(1.5, sVal)) });
              appliedCount++;
            }
          } else if (sVal !== null) {
            setMasterVolume(Math.max(0, Math.min(1.5, sVal)));
            appliedCount++;
          }
        } else if (sType === 'pan' && trackId) {
          const target = tracks.find(t => String(t.id) === String(trackId));
          if (target && sVal !== null) {
            updateTrack(target.id, { pan: Math.max(-1, Math.min(1, sVal)) });
            appliedCount++;
          }
        } else if (sType === 'eq' && trackId) {
          const target = tracks.find(t => String(t.id) === String(trackId));
          if (target && suggestion.parameter) {
            const param = suggestion.parameter.toLowerCase();
            const currentEq = target.eq || { bass: 0, mid: 0, treble: 0 };
            const newEq = { ...currentEq };
            if (param.includes('bass') || param.includes('low')) newEq.bass = sVal ?? 0;
            else if (param.includes('mid')) newEq.mid = sVal ?? 0;
            else if (param.includes('treble') || param.includes('high')) newEq.treble = sVal ?? 0;
            updateTrack(target.id, { eq: newEq });
            appliedCount++;
          }
        }
      });
      
      const score = data.mixScore || 0;
      const assessment = data.overallAssessment || '';
      
      toast.dismiss(mixToast);
      toast.success(
        <div className="space-y-1">
          <div className="font-semibold flex items-center gap-2">
            Quick Mix Complete — Score: {score}/100
            {mixPreferences && <span className="text-[10px] text-purple-400 bg-purple-500/10 px-1.5 py-0.5 rounded-full">PRO</span>}
          </div>
          <div className="text-xs text-zinc-400">
            {appliedCount} adjustment{appliedCount !== 1 ? 's' : ''} applied
            {assessment && ` · ${assessment}`}
          </div>
          {data.tips && data.tips.length > 0 && (
            <div className="text-xs text-amber-400 mt-1">
              Tip: {data.tips[0]}
            </div>
          )}
        </div>,
        { duration: 6000 }
      );
    } catch (err) {
      toast.dismiss(mixToast);
      toast.error('Quick Mix failed — ' + (err.message || 'try again'));
    } finally {
      setIsQuickMixing(false);
    }
  }, [tracks, bpm, masterVolume, projectId, projectName, API_URL, isQuickMixing, updateTrack, setMasterVolume, mixPreferences]);
  
  // Seek to position
  const seekTo = (time) => {
    const wasPlaying = isPlaying;
    if (wasPlaying) stopAll();
    setCurrentTime(Math.max(0, Math.min(time, totalDuration)));
    if (wasPlaying) {
      setTimeout(() => playAll(), 50);
    }
  };
  
  // Export mix
  const exportMix = async (format = 'wav') => {
    if (!audioContextRef.current) return;
    
    // Check if user has access to MP3 export (Pro+ feature)
    if (format === 'mp3') {
      const allowedFormats = getFeatureLimit('export_formats') || ['wav'];
      if (!allowedFormats.includes('mp3')) {
        toast.error(
          <div className="flex items-center gap-2">
            <span>MP3 export requires Pro subscription!</span>
            <button 
              onClick={() => navigate('/pricing')}
              className="px-2 py-1 bg-amber-500 text-black text-xs rounded font-medium"
            >
              Upgrade
            </button>
          </div>,
          { duration: 5000 }
        );
        return;
      }
    }
    
    setIsExporting(true);
    setExportProgress({ stage: 'rendering', percent: 0 });
    
    try {
      // Create offline context for rendering
      const maxDuration = Math.max(...tracks.map(t => t.startTime + t.duration));
      if (maxDuration <= 0) {
        toast.error('No audio to export');
        setIsExporting(false);
        setExportProgress(null);
        return;
      }
      
      const offlineCtx = new OfflineAudioContext(2, maxDuration * SAMPLE_RATE, SAMPLE_RATE);
      
      // Create 15x makeup gain + limiter for export (mirrors live playback chain)
      const exportMakeupGain = offlineCtx.createGain();
      exportMakeupGain.gain.value = 15.0;
      const exportLimiter = offlineCtx.createDynamicsCompressor();
      exportLimiter.threshold.value = -1;
      exportLimiter.knee.value = 3;
      exportLimiter.ratio.value = 20;
      exportLimiter.attack.value = 0.001;
      exportLimiter.release.value = 0.05;
      exportMakeupGain.connect(exportLimiter);
      exportLimiter.connect(offlineCtx.destination);
      
      // Check solo
      const hasSolo = tracks.some(t => t.solo);
      
      // Render each track
      tracks.forEach(track => {
        if (!track.audioBuffer) return;
        if (track.muted) return;
        if (hasSolo && !track.solo) return;
        
        const source = offlineCtx.createBufferSource();
        source.buffer = track.audioBuffer;
        
        const gainNode = offlineCtx.createGain();
        gainNode.gain.value = track.volume * masterVolume;
        
        const pannerNode = offlineCtx.createStereoPanner();
        pannerNode.pan.value = track.pan;
        
        source.connect(gainNode);
        gainNode.connect(pannerNode);
        pannerNode.connect(exportMakeupGain);
        
        source.start(track.startTime);
      });
      
      setExportProgress({ stage: 'rendering', percent: 30 });
      
      // Render
      const renderedBuffer = await offlineCtx.startRendering();
      
      setExportProgress({ stage: 'encoding', percent: 50 });
      
      let blob;
      let mimeType;
      let fileExtension;
      
      if (format === 'mp3') {
        // Convert to MP3 using lamejs
        const mp3Data = await encodeToMp3(renderedBuffer, (progress) => {
          setExportProgress({ stage: 'encoding', percent: 50 + progress * 45 });
        });
        blob = new Blob([mp3Data], { type: 'audio/mp3' });
        mimeType = 'audio/mp3';
        fileExtension = 'mp3';
      } else {
        // Convert to WAV
        const wav = audioBufferToWav(renderedBuffer);
        blob = new Blob([wav], { type: 'audio/wav' });
        mimeType = 'audio/wav';
        fileExtension = 'wav';
      }
      
      setExportProgress({ stage: 'downloading', percent: 95 });
      
      // Download
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName}.${fileExtension}`;
      document.body.appendChild(a);
      a.click();
      // Delay revoking to ensure download starts
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
      
      setExportProgress({ stage: 'complete', percent: 100 });
      toast.success(`Exported as ${fileExtension.toUpperCase()}!`);
      
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export. Please try again.');
    }
    
    setTimeout(() => {
      setIsExporting(false);
      setExportProgress(null);
    }, 500);
  };
  
  // MP3 encoding function using lamejs
  const encodeToMp3 = async (audioBuffer, onProgress) => {
    return new Promise((resolve) => {
      const channels = audioBuffer.numberOfChannels;
      const sampleRate = audioBuffer.sampleRate;
      const kbps = 128; // MP3 bitrate
      
      const mp3encoder = new lamejs.Mp3Encoder(channels, sampleRate, kbps);
      const mp3Data = [];
      
      // Get channel data
      const left = audioBuffer.getChannelData(0);
      const right = channels > 1 ? audioBuffer.getChannelData(1) : left;
      
      // Convert float32 to int16
      const leftInt = new Int16Array(left.length);
      const rightInt = new Int16Array(right.length);
      
      for (let i = 0; i < left.length; i++) {
        leftInt[i] = Math.max(-32768, Math.min(32767, left[i] * 32767));
        rightInt[i] = Math.max(-32768, Math.min(32767, right[i] * 32767));
      }
      
      // Encode in chunks
      const sampleBlockSize = 1152; // Must be multiple of 576
      const totalSamples = leftInt.length;
      let processedSamples = 0;
      
      const processChunk = () => {
        const start = processedSamples;
        const end = Math.min(processedSamples + sampleBlockSize, totalSamples);
        
        if (start < totalSamples) {
          const leftChunk = leftInt.subarray(start, end);
          const rightChunk = rightInt.subarray(start, end);
          
          const mp3buf = mp3encoder.encodeBuffer(leftChunk, rightChunk);
          if (mp3buf.length > 0) {
            mp3Data.push(mp3buf);
          }
          
          processedSamples = end;
          onProgress(processedSamples / totalSamples);
          
          // Continue processing
          setTimeout(processChunk, 0);
        } else {
          // Finalize
          const mp3buf = mp3encoder.flush();
          if (mp3buf.length > 0) {
            mp3Data.push(mp3buf);
          }
          
          // Combine all MP3 data
          const totalLength = mp3Data.reduce((acc, buf) => acc + buf.length, 0);
          const result = new Uint8Array(totalLength);
          let offset = 0;
          for (const buf of mp3Data) {
            result.set(buf, offset);
            offset += buf.length;
          }
          
          resolve(result);
        }
      };
      
      processChunk();
    });
  };
  
  // Mastering export using backend API
  const exportWithMastering = async (presetId) => {
    if (!audioContextRef.current) return;
    
    setIsMasteringExporting(true);
    
    try {
      // Get the preset
      const preset = EXPORT_PRESETS[presetId];
      if (!preset) {
        toast.error('Invalid preset selected');
        return;
      }
      
      toast.info(`Preparing mastering export with "${preset.name}" preset...`);
      
      // First, render all tracks to a single buffer (same as exportMix)
      const maxDuration = Math.max(...tracks.map(t => t.startTime + t.duration));
      if (maxDuration <= 0) {
        toast.error('No audio to export');
        return;
      }
      
      const offlineCtx = new OfflineAudioContext(2, maxDuration * SAMPLE_RATE, SAMPLE_RATE);
      
      // Create makeup gain + limiter for AI mastering export
      const exportMakeupGain = offlineCtx.createGain();
      exportMakeupGain.gain.value = 15.0;
      const exportLimiter2 = offlineCtx.createDynamicsCompressor();
      exportLimiter2.threshold.value = -1;
      exportLimiter2.knee.value = 3;
      exportLimiter2.ratio.value = 20;
      exportLimiter2.attack.value = 0.001;
      exportLimiter2.release.value = 0.05;
      exportMakeupGain.connect(exportLimiter2);
      exportLimiter2.connect(offlineCtx.destination);
      
      // Check solo
      const hasSolo = tracks.some(t => t.solo);
      
      // Render each track
      tracks.forEach(track => {
        if (!track.audioBuffer) return;
        if (track.muted) return;
        if (hasSolo && !track.solo) return;
        
        const source = offlineCtx.createBufferSource();
        source.buffer = track.audioBuffer;
        
        const gainNode = offlineCtx.createGain();
        gainNode.gain.value = track.volume * masterVolume;
        
        const pannerNode = offlineCtx.createStereoPanner();
        pannerNode.pan.value = track.pan;
        
        source.connect(gainNode);
        gainNode.connect(pannerNode);
        pannerNode.connect(exportMakeupGain);
        
        source.start(track.startTime);
      });
      
      // Render
      toast.info('Rendering mix...');
      const renderedBuffer = await offlineCtx.startRendering();
      
      // Convert to WAV base64
      const wavBlob = audioBufferToWavBlob(renderedBuffer);
      const base64 = await blobToBase64(wavBlob);
      
      // Send to backend for mastering
      toast.info('Applying mastering...');
      
      const response = await fetch(`${API_URL}/api/mastering/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          audio_data: base64,
          preset: {
            preset_id: preset.id,
            target_lufs: preset.targetLUFS,
            true_peak_limit: preset.truePeakLimit || -1,
            format: preset.format || 'wav',
            sample_rate: preset.sampleRate || 44100,
            bit_depth: preset.bitDepth || 16,
            normalize: preset.normalize !== false,
            limiter: preset.limiter !== false
          }
        })
      });
      
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Mastering failed');
      }
      
      const result = await response.json();
      
      // Convert base64 back to blob and download
      const masteredBlob = base64ToBlob(result.audio_data, 'audio/wav');
      const url = URL.createObjectURL(masteredBlob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${projectName}_mastered_${preset.id}.${result.format}`;
      document.body.appendChild(a);
      a.click();
      setTimeout(() => {
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }, 1000);
      
      toast.success(`Exported with "${preset.name}" preset! LUFS: ${result.lufs?.integrated?.toFixed(1) || 'N/A'}`);
      
    } catch (error) {
      console.error('Mastering export error:', error);
      toast.error(`Mastering export failed: ${error.message}`);
    }
    
    setIsMasteringExporting(false);
  };
  
  // Helper: Convert AudioBuffer to WAV Blob
  const audioBufferToWavBlob = (buffer) => {
    const numChannels = buffer.numberOfChannels;
    const sampleRate = buffer.sampleRate;
    const bitDepth = 16;
    
    const bytesPerSample = bitDepth / 8;
    const blockAlign = numChannels * bytesPerSample;
    
    const leftChannel = buffer.getChannelData(0);
    const rightChannel = numChannels > 1 ? buffer.getChannelData(1) : leftChannel;
    const length = leftChannel.length;
    
    const dataLength = length * numChannels * bytesPerSample;
    const bufferLength = 44 + dataLength;
    const arrayBuffer = new ArrayBuffer(bufferLength);
    const view = new DataView(arrayBuffer);
    
    // RIFF header
    const writeString = (offset, string) => {
      for (let i = 0; i < string.length; i++) {
        view.setUint8(offset + i, string.charCodeAt(i));
      }
    };
    
    writeString(0, 'RIFF');
    view.setUint32(4, 36 + dataLength, true);
    writeString(8, 'WAVE');
    writeString(12, 'fmt ');
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, numChannels, true);
    view.setUint32(24, sampleRate, true);
    view.setUint32(28, sampleRate * blockAlign, true);
    view.setUint16(32, blockAlign, true);
    view.setUint16(34, bitDepth, true);
    writeString(36, 'data');
    view.setUint32(40, dataLength, true);
    
    // Write audio data
    let offset = 44;
    for (let i = 0; i < length; i++) {
      const leftSample = Math.max(-1, Math.min(1, leftChannel[i]));
      view.setInt16(offset, leftSample * 0x7fff, true);
      offset += 2;
      
      if (numChannels === 2) {
        const rightSample = Math.max(-1, Math.min(1, rightChannel[i]));
        view.setInt16(offset, rightSample * 0x7fff, true);
        offset += 2;
      }
    }
    
    return new Blob([arrayBuffer], { type: 'audio/wav' });
  };
  
  // Helper: Convert blob to base64
  const blobToBase64 = (blob) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result.split(',')[1];
        resolve(base64);
      };
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  };
  
  // Helper: Convert base64 to blob
  const base64ToBlob = (base64, mimeType) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: mimeType });
  };
  
  // Duplicate project (save as new)
  const duplicateProject = async () => {
    if (!token || !projectId) {
      toast.error('Save the project first before duplicating');
      return;
    }
    
    setIsSaving(true);
    
    try {
      const response = await fetch(`${API_URL}/api/studio/projects/${projectId}/duplicate`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error('Failed to duplicate project');
      }
      
      const newProject = await response.json();
      
      // Navigate to the duplicated project
      toast.success(`Duplicated as "${newProject.name}"`);
      navigate(`/studio?project=${newProject.id}`);
      
    } catch (error) {
      console.error('Duplicate error:', error);
      toast.error('Failed to duplicate project');
    }
    
    setIsSaving(false);
  };
  
  // Save project
  const saveProject = async () => {
    if (!token) {
      toast.error('Please log in to save projects');
      return;
    }
    
    setIsSaving(true);
    setSaveProgress(null);
    
    try {
      // Serialize tracks (without audio buffers - those need separate handling)
      const projectData = {
        name: projectName,
        bpm,
        masterVolume,
        tracks: tracks.map(t => ({
          id: t.id,
          name: t.name,
          type: t.type || null,
          instrumentType: t.instrumentType || null,
          volume: t.volume,
          pan: t.pan,
          muted: t.muted,
          solo: t.solo,
          eq: t.eq,
          effects: t.effects,
          startTime: t.startTime,
          duration: t.duration,
          hasAudio: !!t.audioBuffer,
          midiEvents: t.midiEvents || null,
          midiClips: t.midiClips || null,
          automationLanes: t.automationLanes || null,
          effectChain: t.effectChain || null,
          pluginChain: t.pluginChain ? t.pluginChain.map(p => ({
            id: p.id,
            type: p.name?.toLowerCase().replace(' ', '-') || p.type,
            enabled: p.enabled,
            params: (() => {
              const params = {};
              if (p.parameters instanceof Map) {
                p.parameters.forEach((param, id) => {
                  params[id] = param.getValue ? param.getValue() : param.value;
                });
              }
              return params;
            })()
          })) : null,
        })),
      };
      
      // If updating existing project, use PUT; otherwise POST
      const method = projectId ? 'PUT' : 'POST';
      const url = projectId 
        ? `${API_URL}/api/studio/projects/${projectId}`
        : `${API_URL}/api/studio/projects`;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(projectData),
      });
      
      if (!response.ok) {
        throw new Error('Failed to save project metadata');
      }
      
      const savedProject = await response.json();
      const savedProjectId = savedProject.id;
      setProjectId(savedProjectId);
      
      // Upload audio for tracks that have audio buffers
      const tracksWithAudio = tracks.filter(t => t.audioBuffer);
      
      if (tracksWithAudio.length > 0) {
        let totalChunks = 0;
        let uploadedChunks = 0;
        
        // Calculate total chunks for progress
        for (const track of tracksWithAudio) {
          const base64 = await audioBufferToBase64(track.audioBuffer);
          totalChunks += Math.ceil(base64.length / CHUNK_SIZE);
        }
        
        setSaveProgress({ current: 0, total: totalChunks });
        
        // Upload each track's audio
        for (const track of tracksWithAudio) {
          const base64Audio = await audioBufferToBase64(track.audioBuffer);
          const chunks = splitIntoChunks(base64Audio);
          
          for (let i = 0; i < chunks.length; i++) {
            const chunkResponse = await fetch(`${API_URL}/api/studio/audio/upload`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
              },
              body: JSON.stringify({
                project_id: savedProjectId,
                track_id: track.id,
                chunk_index: i,
                total_chunks: chunks.length,
                audio_data: chunks[i],
              }),
            });
            
            if (!chunkResponse.ok) {
              throw new Error(`Failed to upload audio chunk ${i + 1}/${chunks.length}`);
            }
            
            uploadedChunks++;
            setSaveProgress({ current: uploadedChunks, total: totalChunks });
          }
        }
      }
      
      setSaveProgress(null);
      toast.success('Project saved with audio!');
      
      // Update URL to include project ID
      if (!projectId) {
        navigate(`/studio?project=${savedProjectId}`, { replace: true });
      }
      
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save project');
      setSaveProgress(null);
    }
    
    setIsSaving(false);
  };
  
  // Format time display
  // formatTime from ./utils
  const formatTime = formatTimeUtil;

  return (
    <TooltipProvider>
    <div 
      className="h-screen bg-zinc-950 text-white flex flex-col overflow-hidden relative"
    >
      {/* iOS Audio Unlock Banner - appears when audio context is suspended */}
      {isIOS && audioContextRef.current?.state === 'suspended' && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-gradient-to-r from-orange-500 to-amber-500 text-white px-4 py-3 flex items-center justify-between z-50"
        >
          <span className="text-sm font-medium">
            🎸 Tap to enable audio for your iRig/audio interface
          </span>
          <Button
            size="sm"
            variant="secondary"
            className="bg-white/20 hover:bg-white/30 text-white"
            onClick={async () => {
              if (audioContextRef.current?.state === 'suspended') {
                await audioContextRef.current.resume();
                toast.success('Audio enabled! Your iRig is ready.');
              }
            }}
          >
            Enable Audio
          </Button>
        </motion.div>
      )}
      
      {/* Main content wrapper */}
      <div className="relative z-10 flex flex-col h-full">
      
      {/* Top Transport Bar */}
      <HeaderToolbar
        // Project
        projectName={projectName}
        setProjectName={setProjectName}
        projectId={projectId}
        
        // Transport
        isPlaying={isPlaying}
        currentTime={currentTime}
        playAll={playAll}
        stopAll={stopAll}
        seekTo={seekTo}
        setCurrentTime={setCurrentTime}
        showCollaboration={showCollaboration}
        formatTime={formatTime}
        
        // BPM
        bpm={bpm}
        setBpm={setBpm}
        pushUndoAction={pushUndoAction}
        ActionTypes={ActionTypes}
        
        // Metronome
        metronomeEnabled={metronomeEnabled}
        setMetronomeEnabled={setMetronomeEnabled}
        metronomeVolume={metronomeVolume}
        setMetronomeVolume={setMetronomeVolume}
        metronomeGainRef={metronomeGainRef}
        currentBeat={currentBeat}
        
        // Loop
        loopEnabled={loopEnabled}
        setLoopEnabled={setLoopEnabled}
        loopStart={loopStart}
        loopEnd={loopEnd}
        
        // Input Monitor
        inputMonitorEnabled={inputMonitorEnabled}
        setInputMonitorEnabled={setInputMonitorEnabled}
        isRecording={isRecording}
        inputLevel={inputLevel}
        
        // Audio Input Device
        selectedAudioInput={selectedAudioInput}
        setSelectedAudioInput={setSelectedAudioInput}
        inputGain={inputGain}
        setInputGain={setInputGain}
        
        // Editing
        clipEditMode={clipEditMode}
        setClipEditMode={setClipEditMode}
        selectedClipsForJoin={selectedClipsForJoin}
        setSelectedClipsForJoin={setSelectedClipsForJoin}
        showMarkersPanel={showMarkersPanel}
        setShowMarkersPanel={setShowMarkersPanel}
        markersCount={markers.length}
        onJoinClips={() => {
          const clipsByTrack = {};
          selectedClipsForJoin.forEach(({trackId, clipId}) => {
            if (!clipsByTrack[trackId]) clipsByTrack[trackId] = [];
            clipsByTrack[trackId].push(clipId);
          });
          Object.entries(clipsByTrack).forEach(([trackId, clipIds]) => {
            if (clipIds.length >= 2) {
              handleJoinMidiClips(parseInt(trackId), clipIds);
            }
          });
        }}
        
        // Grid Snap
        snapEnabled={snapEnabled}
        setSnapEnabled={setSnapEnabled}
        
        // Automation
        showAutomation={showAutomation}
        setShowAutomation={setShowAutomation}
        
        // Instruments
        showInstruments={showInstruments}
        setShowInstruments={setShowInstruments}
        isMidiRecording={isMidiRecording}
        
        // MIDI
        midiDevices={midiDevices}
        showMIDIPanel={showMIDIPanel}
        setShowMIDIPanel={setShowMIDIPanel}
        midiActivity={midiActivity}
        
        // View Mode
        viewMode={viewMode}
        setViewMode={setViewMode}
        
        // Undo/Redo
        canUndo={canUndo}
        canRedo={canRedo}
        undo={undo}
        redo={redo}
        
        // Panels
        showSmartTempo={showSmartTempo}
        setShowSmartTempo={setShowSmartTempo}
        showChordTrack={showChordTrack}
        setShowChordTrack={setShowChordTrack}
        setShowChordWizard={setShowChordWizard}
        showEffects={showEffects}
        setShowEffects={setShowEffects}
        showMastering={showMastering}
        setShowMastering={setShowMastering}
        showPluginRack={showPluginRack}
        setShowPluginRack={setShowPluginRack}
        setShowQuickStart={setShowQuickStart}
        showAIMixing={showAIMixing}
        setShowAIMixing={setShowAIMixing}
        showAIMastering={showAIMastering}
        setShowAIMastering={setShowAIMastering}
        audioCharacteristics={audioCharacteristics}
        collabSessionId={collabSessionId}
        setShowSessionManager={setShowSessionManager}
        setShowCollaboration={setShowCollaboration}
        activeJamSession={activeJamSession}
        showJamSessionPanel={showJamSessionPanel}
        setShowJamSessionPanel={setShowJamSessionPanel}
        setShowJamSessionModal={setShowJamSessionModal}
        setShowAIMixAssistant={setShowAIMixAssistant}
        setShowAIChordGenerator={setShowAIChordGenerator}
        setShowAILyricAssistant={setShowAILyricAssistant}
        setShowAIDrumGenerator={setShowAIDrumGenerator}
        setShowVideoSync={setShowVideoSync}
        setShowSampleLibrary={setShowSampleLibrary}
        setShowPluginBrowser={setShowPluginBrowser}
        setShowPluginMarketplace={setShowPluginMarketplace}
        setShowKeyboardShortcuts={setShowKeyboardShortcuts}
        
        // Simplified Mode
        isSimplifiedMode={isSimplifiedMode}
        setIsSimplifiedMode={setIsSimplifiedMode}
        
        // Theme
        isDark={isDark}
        toggleTheme={toggleTheme}
        
        // Tour
        startTour={startTour}
        
        // Navigation
        navigate={navigate}
        
        // Template
        setShowTemplateSelector={setShowTemplateSelector}
        
        // Save/Export
        saveProject={saveProject}
        isSaving={isSaving}
        duplicateProject={duplicateProject}
        isExporting={isExporting}
        onExportWav={() => exportMix('wav')}
        onExportMp3={() => exportMix('mp3')}
        setShowBatchExporter={setShowBatchExporter}
        canExportMp3={(getFeatureLimit('export_formats') || ['wav']).includes('mp3')}
        
        // Quick Mix
        onQuickMix={handleQuickMix}
        isQuickMixing={isQuickMixing}
        onOpenQuickMixPro={() => setShowQuickMixPro(true)}
        hasProPreferences={!!mixPreferences}
        
        // User
        user={user}
        planId={planId}
        logout={logout}
      />
      
      {/* Overlays (Count-In, Export Progress, Loading, Save Progress) */}
      <DAWOverlays
        isCountingIn={isCountingIn}
        countInProgress={countInProgress}
        countInBeats={countInBeats}
        exportProgress={exportProgress}
        isLoading={isLoading}
        saveProgress={saveProgress}
      />
      
      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Track List / Mixer - Collapsible on mobile */}
        <TrackSidebarWrapper
          showMixer={showMixer}
          setShowMixer={setShowMixer}
          isMobile={isMobile}
          tracks={tracks}
          selectedTrack={selectedTrack}
          setSelectedTrack={setSelectedTrack}
          recordingTrack={recordingTrack}
          isMidiRecording={isMidiRecording}
          effectiveMaxTracks={effectiveMaxTracks}
          subscriptionTrackLimit={subscriptionTrackLimit}
          maxTracks={MAX_TRACKS}
          addTrack={addTrack}
          addInstrumentTrack={addInstrumentTrack}
          updateTrack={updateTrack}
          removeTrack={removeTrack}
          duplicateTrack={duplicateTrack}
          isRecording={isRecording}
          startRecording={startRecording}
          stopRecording={stopRecording}
          startMidiRecording={startMidiRecording}
          stopMidiRecording={stopMidiRecording}
          handleFileImport={handleFileImport}
          masterVolume={masterVolume}
          setMasterVolume={setMasterVolume}
          getFeatureLimit={getFeatureLimit}
          draggedTrackId={draggedTrackId}
          dragOverTrackId={dragOverTrackId}
          dropPosition={dropPosition}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          handleDragEnd={handleDragEnd}
          showCollaboration={showCollaboration}
          collabUsers={collabUsers}
          currentUserId={user?.id}
          navigate={navigate}
        />
        
        {/* Timeline / Arrangement - EDIT VIEW */}
        {viewMode === 'edit' && (
          <TimelineContent
            // Timeline dimensions
            totalDuration={totalDuration}
            zoom={zoom}
            setZoom={setZoom}
            
            // Playback
            currentTime={currentTime}
            isPlaying={isPlaying}
            seekTo={seekTo}
            bpm={bpm}
            
            // Tracks
            tracks={tracks}
            selectedTrack={selectedTrack}
            setSelectedTrack={setSelectedTrack}
            
            // Collaboration
            showCollaboration={showCollaboration}
            remoteCursors={remoteCursors}
            collabUsers={collabUsers}
            currentUserId={user?.id}
            
            // Loop
            loopEnabled={loopEnabled}
            loopStart={loopStart}
            loopEnd={loopEnd}
            setIsDraggingLoopStart={setIsDraggingLoopStart}
            setIsDraggingLoopEnd={setIsDraggingLoopEnd}
            loopDragStartRef={loopDragStartRef}
            
            // Chord Track
            showChordTrack={showChordTrack}
            chordRegions={chordRegions}
            setChordRegions={setChordRegions}
            snapEnabled={snapEnabled}
            
            // Automation
            showAutomation={showAutomation}
            automationSnapEnabled={automationSnapEnabled}
            addAutomationLane={addAutomationLane}
            deleteAutomationLane={deleteAutomationLane}
            updateAutomationLane={updateAutomationLane}
            toggleAutomationLaneVisibility={toggleAutomationLaneVisibility}
            
            // Clip Operations
            clipEditMode={clipEditMode}
            selectedClipsForJoin={selectedClipsForJoin}
            setSelectedClipsForJoin={setSelectedClipsForJoin}
            openClipInPianoRoll={openClipInPianoRoll}
            handleMoveClip={handleMoveClip}
            handleResizeClip={handleResizeClip}
            createEmptyMidiClip={createEmptyMidiClip}
            handleSplitMidiClip={handleSplitMidiClip}
            handleSplitAudioClip={handleSplitAudioClip}
            handleJoinMidiClips={handleJoinMidiClips}
            
            // Waveform
            handleFadeChange={handleFadeChange}
            handleLoopPointsChange={handleLoopPointsChange}
            handleTrimChange={handleTrimChange}
            handleSelectAudioClip={handleSelectAudioClip}
            selectedAudioClip={selectedAudioClip}
            audioSelection={audioSelection}
            handleAudioSelectionChange={handleAudioSelectionChange}
            
            // Mobile
            isMobile={isMobile}
            showMixer={showMixer}
            setShowMixer={setShowMixer}
            
            // Time Signature
            timeSignature={timeSignature}
            
            // Sample drop handler (P2 feature)
            onSampleDrop={handleSampleDrop}
          />
        )}
        
        {/* MIX VIEW - Pro Tools Style Full Mixer */}
        <MixViewWrapper
          viewMode={viewMode}
          tracks={tracks}
          masterVolume={masterVolume}
          selectedTrackId={selectedTrack}
          handleMixerVolumeChange={handleMixerVolumeChange}
          handleMixerPanChange={handleMixerPanChange}
          handleMixerMuteToggle={handleMixerMuteToggle}
          handleMixerSoloToggle={handleMixerSoloToggle}
          setSelectedTrack={setSelectedTrack}
          handleMixerNameChange={handleMixerNameChange}
          setMasterVolume={setMasterVolume}
          handleAddEffect={handleAddEffect}
          handleRemoveEffect={handleRemoveEffect}
          handleToggleEffect={handleToggleEffect}
          handleReorderEffects={handleReorderEffects}
          handleEffectParamChange={handleEffectParamChange}
          draggedTrackId={draggedTrackId}
          dragOverTrackId={dragOverTrackId}
          dropPosition={dropPosition}
          handleDragStart={handleDragStart}
          handleDragOver={handleDragOver}
          handleDragLeave={handleDragLeave}
          handleDrop={handleDrop}
          handleDragEnd={handleDragEnd}
        />
        
        {/* Effects Panel - Right Side */}
        <EffectsSidebarWrapper
          isOpen={showEffects}
          onClose={() => setShowEffects(false)}
          isMobile={isMobile}
          track={tracks.find(t => t.id === selectedTrack)}
          onUpdate={(updates) => updateTrack(selectedTrack, updates)}
          onOpenAdvancedEffects={() => {
            setSelectedEffectsTrackId(selectedTrack);
            setShowAdvancedEffects(true);
          }}
          isPro={isPro || isStudio}
          metronomeEnabled={metronomeEnabled}
          setMetronomeEnabled={setMetronomeEnabled}
          metronomeVolume={metronomeVolume}
          setMetronomeVolume={setMetronomeVolume}
          timeSignature={timeSignature}
          setTimeSignature={setTimeSignature}
          showCollaboration={showCollaboration}
          currentBeat={currentBeat}
          countInEnabled={countInEnabled}
          setCountInEnabled={setCountInEnabled}
          countInBeats={countInBeats}
          setCountInBeats={setCountInBeats}
        />
      </div>
      
      {/* Virtual Instruments Panel */}
      <InstrumentsPanelWrapper
        isOpen={showInstruments}
        onClose={() => setShowInstruments(false)}
        onNoteOn={handleMidiNoteOn}
        onNoteOff={handleMidiNoteOff}
        onDrumHit={handleDrumHit}
        activeNotes={activeNotes}
        activePads={activePads}
        isRecording={isMidiRecording}
        selectedInstrument={selectedInstrument}
        onInstrumentChange={setSelectedInstrument}
      />
      
      {/* Piano Roll Modal */}
      <PianoRollWrapper
        isOpen={showPianoRoll}
        editingClip={editingClip}
        editingTrackId={editingTrackId}
        tracks={tracks}
        bpm={bpm}
        timeSignature={timeSignature}
        isPlaying={isPlaying}
        currentTime={currentTime}
        onClipUpdate={handlePianoRollClipUpdate}
        onClose={closePianoRoll}
        synthRef={synthRef}
        drumSynthRef={drumSynthRef}
        DRUM_KIT={DRUM_KIT}
        undo={undo}
        redo={redo}
        canUndo={canUndo}
        canRedo={canRedo}
      />
      
      {/* Waveform Editing Toolbar */}
      <WaveformToolbarWrapper
        isVisible={waveformEditMode && selectedAudioClip}
        hasSelection={audioSelection !== null}
        hasClipboard={audioClipboard !== null}
        canUndo={canUndo}
        canRedo={canRedo}
        isPlaying={isPlaying}
        loopEnabled={loopEnabled}
        onCut={handleCutAudio}
        onCopy={handleCopyAudio}
        onPaste={handlePasteAudio}
        onDelete={handleDeleteAudioSelection}
        onUndo={undo}
        onRedo={redo}
        onZoomIn={() => setZoom(prev => Math.min(prev * 1.5, 8))}
        onZoomOut={() => setZoom(prev => Math.max(prev / 1.5, 0.25))}
        onToggleLoop={() => setLoopEnabled(!loopEnabled)}
        onPlay={playAll}
        onPause={stopAll}
        onStop={() => { stopAll(); setCurrentTime(0); }}
      />
      
      {/* Mixer Panel - Only show in Edit view */}
      <MixerPanelWrapper
        viewMode={viewMode}
        tracks={tracks}
        masterVolume={masterVolume}
        selectedTrackId={selectedTrack}
        handleMixerVolumeChange={handleMixerVolumeChange}
        handleMixerPanChange={handleMixerPanChange}
        handleMixerMuteToggle={handleMixerMuteToggle}
        handleMixerSoloToggle={handleMixerSoloToggle}
        setSelectedTrack={setSelectedTrack}
        handleMixerNameChange={handleMixerNameChange}
        removeTrack={removeTrack}
        setMasterVolume={setMasterVolume}
        handleAddEffect={handleAddEffect}
        handleRemoveEffect={handleRemoveEffect}
        handleToggleEffect={handleToggleEffect}
        handleReorderEffects={handleReorderEffects}
        handleEffectParamChange={handleEffectParamChange}
        handleOpenTrackPluginBrowser={handleOpenTrackPluginBrowser}
        handleRemoveTrackPlugin={handleRemoveTrackPlugin}
        handleToggleTrackPluginBypass={handleToggleTrackPluginBypass}
        handleReorderTrackPlugins={handleReorderTrackPlugins}
        handleTrackPluginParamChange={handleTrackPluginParamChange}
        handleOpenTrackPresets={handleOpenTrackPresets}
        handleGuitarEffectsChange={handleGuitarEffectsChange}
        expandedGuitarEffectsTrack={expandedGuitarEffectsTrack}
        setExpandedGuitarEffectsTrack={setExpandedGuitarEffectsTrack}
        userId={user?.id}
        showMixer={showMixer}
        setShowMixer={setShowMixer}
      />
      
      {/* All Modals and Panels Container */}
      <DAWModalsContainer
        // Chord Wizard
        showChordWizard={showChordWizard}
        setShowChordWizard={setShowChordWizard}
        handleChordWizardInsert={handleChordWizardInsert}
        handleChordWizardAddToChordTrack={handleChordWizardAddToChordTrack}
        audioContextRef={audioContextRef}
        
        // Smart Tempo
        showSmartTempo={showSmartTempo}
        setShowSmartTempo={setShowSmartTempo}
        tracks={tracks}
        bpm={bpm}
        setBpm={setBpm}
        
        // Quick Start
        showQuickStart={showQuickStart}
        setShowQuickStart={setShowQuickStart}
        handleQuickStartTemplate={handleQuickStartTemplate}
        
        // AI Mixing
        showAIMixing={showAIMixing}
        setShowAIMixing={setShowAIMixing}
        masterVolume={masterVolume}
        projectName={projectName}
        setMasterVolume={setMasterVolume}
        updateTrack={updateTrack}
        
        // Mastering
        showMastering={showMastering}
        setShowMastering={setShowMastering}
        exportWithMastering={exportWithMastering}
        isMasteringExporting={isMasteringExporting}
        
        // AI Mastering
        showAIMastering={showAIMastering}
        setShowAIMastering={setShowAIMastering}
        projectId={projectId}
        audioCharacteristics={audioCharacteristics}
        handleApplyMasteringSuggestion={handleApplyMasteringSuggestion}
        
        // Collaboration
        showCollaboration={showCollaboration}
        setShowCollaboration={setShowCollaboration}
        collabSessionId={collabSessionId}
        user={user}
        isPlaying={isPlaying}
        playAll={playAll}
        stopAll={stopAll}
        setCurrentTime={setCurrentTime}
        setTimeSignature={setTimeSignature}
        setRemoteCursors={setRemoteCursors}
        setCollabUsers={setCollabUsers}
        handleLeaveSession={handleLeaveSession}
        
        // Jam Session Panel
        showJamSessionPanel={showJamSessionPanel}
        setShowJamSessionPanel={setShowJamSessionPanel}
        activeJamSession={activeJamSession}
        setActiveJamSession={setActiveJamSession}
        
        // Plugin Rack
        showPluginRack={showPluginRack}
        setShowPluginRack={setShowPluginRack}
        masterPluginChain={masterPluginChain}
        setShowPluginBrowser={setShowPluginBrowser}
        setShowChainPresets={setShowChainPresets}
        handleRemoveMasterPlugin={handleRemoveMasterPlugin}
        handleToggleMasterPluginBypass={handleToggleMasterPluginBypass}
        handleReorderMasterPlugins={handleReorderMasterPlugins}
        handleMasterPluginParamChange={handleMasterPluginParamChange}
        handleDuplicateMasterPlugin={handleDuplicateMasterPlugin}
        handleResetMasterPlugin={handleResetMasterPlugin}
        
        // Plugin Browser
        showPluginBrowser={showPluginBrowser}
        handleAddMasterPlugin={handleAddMasterPlugin}
        trackPluginBrowserTarget={trackPluginBrowserTarget}
        setTrackPluginBrowserTarget={setTrackPluginBrowserTarget}
        handleAddTrackPlugin={handleAddTrackPlugin}
        
        // Plugin Marketplace
        showPluginMarketplace={showPluginMarketplace}
        setShowPluginMarketplace={setShowPluginMarketplace}
        
        // Chain Presets
        showChainPresets={showChainPresets}
        handleLoadChainPreset={handleLoadChainPreset}
        
        // Track Presets
        trackPresetTarget={trackPresetTarget}
        setTrackPresetTarget={setTrackPresetTarget}
        handleLoadTrackPreset={handleLoadTrackPreset}
        
        // Batch Exporter
        showBatchExporter={showBatchExporter}
        setShowBatchExporter={setShowBatchExporter}
        handleBatchImportTracks={handleBatchImportTracks}
        
        // Session Manager
        showSessionManager={showSessionManager}
        setShowSessionManager={setShowSessionManager}
        handleCreateSession={handleCreateSession}
        handleJoinSession={handleJoinSession}
        
        // Jam Session Modal
        showJamSessionModal={showJamSessionModal}
        setShowJamSessionModal={setShowJamSessionModal}
        
        // Keyboard Shortcuts
        showKeyboardShortcuts={showKeyboardShortcuts}
        setShowKeyboardShortcuts={setShowKeyboardShortcuts}
        
        // Quick Tour
        showTour={showTour}
        closeTour={closeTour}
        completeTour={completeTour}
        
        // Template Selector
        showTemplateSelector={showTemplateSelector}
        setShowTemplateSelector={setShowTemplateSelector}
        handleSelectTemplate={handleSelectTemplate}
        
        // Markers Panel
        showMarkersPanel={showMarkersPanel}
        markers={markers}
        regions={regions}
        addMarker={addMarker}
        updateMarker={updateMarker}
        deleteMarker={deleteMarker}
        addRegion={addRegion}
        updateRegion={updateRegion}
        deleteRegion={deleteRegion}
        playbackStartTimeRef={playbackStartTimeRef}
        currentTime={currentTime}
        totalDuration={totalDuration}
        
        // Marker Picker
        showMarkerPicker={showMarkerPicker}
        setShowMarkerPicker={setShowMarkerPicker}
        pendingMarkerTime={pendingMarkerTime}
        setPendingMarkerTime={setPendingMarkerTime}
        markerPickerPosition={markerPickerPosition}
        
        // Status Bar
        isSaving={isSaving}
        
        // Advanced Effects
        showAdvancedEffects={showAdvancedEffects}
        setShowAdvancedEffects={setShowAdvancedEffects}
        selectedEffectsTrackId={selectedEffectsTrackId}
        advancedEffectsData={advancedEffectsData}
        setAdvancedEffectsData={setAdvancedEffectsData}
        isPro={isPro}
        isStudio={isStudio}
        setShowEffectPresetsBrowser={setShowEffectPresetsBrowser}
        
        // Effect Presets Browser
        showEffectPresetsBrowser={showEffectPresetsBrowser}
        
        // AI Mix Assistant
        showAIMixAssistant={showAIMixAssistant}
        setShowAIMixAssistant={setShowAIMixAssistant}
        setMasterPluginChain={setMasterPluginChain}
        
        // AI Chord Generator
        showAIChordGenerator={showAIChordGenerator}
        setShowAIChordGenerator={setShowAIChordGenerator}
        setTracks={setTracks}
        
        // AI Lyric Assistant
        showAILyricAssistant={showAILyricAssistant}
        setShowAILyricAssistant={setShowAILyricAssistant}
        
        // AI Drum Generator
        showAIDrumGenerator={showAIDrumGenerator}
        setShowAIDrumGenerator={setShowAIDrumGenerator}
        
        // Video Sync
        showVideoSync={showVideoSync}
        setShowVideoSync={setShowVideoSync}
        
        // Sample Library
        showSampleLibrary={showSampleLibrary}
        setShowSampleLibrary={setShowSampleLibrary}
      />
      
      </div> {/* End of main content wrapper */}
      
      {/* Quick Mix Pro Preferences Panel */}
      <QuickMixProPanel
        isOpen={showQuickMixPro}
        onClose={() => setShowQuickMixPro(false)}
        userId={user?.id}
        onPreferencesSaved={(prefs) => setMixPreferences(prefs)}
      />
    </div>
    </TooltipProvider>
  );
};

export default DAWWorkspace;
